import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import api from '../services/api';
import { getPropertyListDisplayData, getPrixDisplay, getPropertyIcon, getStatusBadge } from '../utils/propertyListHelpers';
import { getTransactionBadge, getVille, getLoyerMensuel } from '../utils/propertyDataMapper';
import AffectClientModal from '../components/shared/AffectClientModal';
import SendFichesModal from '../components/property/SendFichesModal';
import { toast } from 'sonner';
import { formatPriceFR, formatRentFR, formatSurfaceFR } from '../utils/formatFR';
import sortSelectOptions from '../utils/sortSelectOptions';
import { propertyMatchesQuery, codesTaxonomiePourSaisie } from '../utils/propertySearch';
import { getPropertyMainPhotoUrl, getPropertyMainPhotoSrcSet } from '../utils/mediaUrl';
import { getNegociateurId } from '../utils/negociateur';
import { OPERATION_LABELS, PROFIL_LABELS } from '../utils/propertyKpis';
import DiffusionBadges, { aUnSupportModifie, supportsModifies } from '../components/property/DiffusionBadges';
import DiffuserEnLotModal from '../components/property/DiffuserEnLotModal';
import DiffusionManagerModal from '../components/property/DiffusionManagerModal';
import SelecteurTelephone from '../components/SelecteurTelephone';
import FacetSearchBar from '../components/property/FacetSearchBar';
import { fetchDiffusionOverview } from '../services/diffusionApi';
import { messageArchivage, messageSuppression, messageGroupe, messageServeur } from '../utils/retraitPortails';

/**
 * PropertiesListPage - Nouvelle v2 (décembre 2024)
 * 
 * FEATURES COMPLÈTES :
 * - Liste + Cartes responsive
 * - Favoris, Menu "...", Actions groupées, 4 colonnes, Détails complets
 * - Filtres avancés (secteur, opération, statut, prix/loyer, surface)
 * - Tri multi-colonnes (nom, prix, surface, date)
 * - Sélection multiple + bulk actions (archiver, restaurer, supprimer)
 * - Génération liens publics + partage
 * - Mode archivés toggle
 * - Navigation canonique vers /properties/new
 * 
 * ✅ Compatible nouvelle structure : taxonomy, public_location, informations_vente/bail
 */

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// lot G — Plafond du dossier groupé. Chaque bien coûte jusqu'à 9 images au PDF, quel que
// soit son nombre de photos : au-delà de 5 biens la génération échoue. Plafond aussi tenu
// par la route `/properties/bulk-fiche-bien` — on ne compte jamais sur l'interface seule.
const MAX_BIENS_DOSSIER = 5;
const MESSAGE_PLAFOND_DOSSIER = 'Maximum 5 biens par dossier. Sélectionnez-en moins, ou générez plusieurs dossiers.';

// lot AG1 — taille de la première page affichée immédiatement (le reste suit en arrière-plan).
const PREMIERE_PAGE = 20;

// lot AV10 — les cinq rubriques cumulatives de la recherche à facettes. « Sans photo »,
// le budget et la surface n'en font PAS partie : rien à dénombrer valeur par valeur.
// lot AV10 / P3-5 — la maquette validée fait foi (mockup-barre-de-filtres-A-VALIDER.html) :
// tout tient sur UNE ligne. `rapide` = sur la ligne (aucun intitulé au-dessus, le bouton porte
// son nom) ; les autres sont sous « Filtres (n) », avec intitulé au-dessus (là c'est justifié :
// on est dans un panneau, pas sur une ligne contrainte).
// ⚠️ Transaction est sur la ligne parce que le budget en DÉPEND : on la choisit d'abord, le
// budget est prêt quand on ouvre le panneau. ⛔ Pas de quatrième déroulant sur la ligne.
const TITRES_FACETTES = [
  { cle: 'villes', titre: 'Ville', vide: 'Ville', accord: 'f', rapide: true },
  { cle: 'objets', titre: 'Type de bien', vide: 'Type de bien', accord: 'm', rapide: true },
  { cle: 'operations', titre: 'Transaction', vide: 'Transaction', accord: 'f', rapide: true },
  { cle: 'profils', titre: 'Catégorie', vide: 'Toutes', accord: 'f' },
  { cle: 'negociateurs', titre: 'Négociateur', vide: 'Tous', accord: 'm' },
  { cle: 'photos', titre: 'Photos', vide: 'Tous', accord: 'm' },
];
const CLES_FACETTES = TITRES_FACETTES.map(f => f.cle);

// Opérations de location — même liste que `utils/propertyKpis.js` : elles décident si le
// champ de budget porte sur un prix ou sur un loyer.
const OPS_LOCATION = ['location', 'location_droit_entree', 'location_gerance'];
const MARGE_BUDGET = 1.1;

// `photosCount` est renvoyé par /properties/list (lot N5) ; repli sur les médias du bien.
const nbPhotosBien = (property) => property.photosCount ?? (property.medias?.photos?.length || 0);

const PropertiesListPage = ({ 
  // Callbacks fournis par le parent (App.js via propertyFormRef)
  consultProperty,
  selectProperty,
  startNewProperty,
  
  // Favoris synchronisés
  favoriteProperties: externalFavorites,
  onToggleFavorite,
  
  // Données utilisateur
  userProfile,
  agencySettings,
  
  // Callbacks optionnels
  onPropertiesChange,
}) => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const location = useLocation();
  // lot AG1 — le portefeuille complet est-il chargé ? (mention discrète près du compteur)
  const [chargementComplet, setChargementComplet] = useState(true);
  const chargementEnCoursRef = useRef(false);
  // Dernière adresse écrite par l'écran lui-même (miroir filtres → URL), pour ne pas la relire.
  const derniereUrlEcrite = useRef(null);

  // lot V1 — l'état de recherche vit dans l'adresse. Avant, `searchFilters`, `currentPage` et
  // `itemsPerPage` ne vivaient que dans l'état du composant : revenir d'une fiche bien les
  // perdait et il fallait retaper la recherche. Valeurs lues UNE fois, au montage.
  const _urlInit = useRef(new URLSearchParams(window.location.search));
  const _urlStr = (k, def = '') => _urlInit.current.get(k) || def;
  const _urlNum = (k, def) => {
    const n = parseInt(_urlInit.current.get(k), 10);
    return Number.isFinite(n) && n > 0 ? n : def;
  };
  
  // === ÉTATS PRINCIPAUX ===
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [propertiesCache, setPropertiesCache] = useState({}); // Cache par statut
  
  // Filtre et compteurs
  const [currentFilter, setCurrentFilter] = useState(searchParams.get('filter') || 'active');
  const [counts, setCounts] = useState({
    all: 0, active: 0, draft: 0, archive: 0, sold: 0, shared: 0, favorites: 0
  });
  
  // Sélection et vue
  const [activeDropdown, setActiveDropdown] = useState(null);
  
  // Fermer le dropdown quand on clique ailleurs
  useEffect(() => {
    const handleClickOutside = () => setActiveDropdown(null);
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, []);
  
  const [selectedProperties, setSelectedProperties] = useState([]);
  const [viewMode, setViewMode] = useState('grid');

  // Mobile (< 768px) : la vue liste est une table desktop illisible.
  // → on FORCE le mode grille et on masque le toggle. En desktop, comportement
  //   inchangé (l'utilisateur reprend la main via le toggle Grille/Liste).
  const [isMobile, setIsMobile] = useState(() =>
    typeof window !== 'undefined' ? window.innerWidth < 768 : false
  );
  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);
  useEffect(() => {
    if (isMobile && viewMode !== 'grid') setViewMode('grid');
  }, [isMobile, viewMode]);
  
  // Pagination
  const [currentPage, setCurrentPage] = useState(() => _urlNum('page', 1));
  const [itemsPerPage, setItemsPerPage] = useState(() => _urlNum('taille', 20));
  
  // Tri
  const [sortColumn, setSortColumn] = useState(() => _urlStr('tri', 'created_at'));
  const [sortOrder, setSortOrder] = useState(() => _urlStr('ordre', 'desc')); // 'asc' ou 'desc'
  
  // Favoris - Toujours utiliser localFavorites pour l'affichage visuel
  const [localFavorites, setLocalFavorites] = useState([]);
  const favoriteProperties = localFavorites;
  
  // lot AV10 — une liste de valeurs par facette. `ancien` = nom du paramètre au SINGULIER
  // encore en circulation dans les liens partagés : il est LU (un lien ancien continue de
  // filtrer), jamais réécrit — le miroir ci-dessous n'écrit que le format pluriel, en
  // remplacement d'entrée d'historique. ⛔ Rupture nette exclue : un lien qui ne filtre plus
  // ouvre le portefeuille entier sans rien dire, et l'utilisateur croit lire sa recherche.
  const _urlListe = (k, ancien) => {
    const brut = _urlStr(k);
    if (brut) return brut.split(',').filter(Boolean);
    const seul = ancien ? _urlStr(ancien) : '';
    return seul ? [seul] : [];
  };

  // Filtres de recherche — initialisés depuis l'adresse (lot V1, facettes lot AV10)
  const [searchFilters, setSearchFilters] = useState(() => ({
    reference: _urlStr('reference'),
    villes: _urlListe('villes', 'ville'),
    negociateurs: _urlListe('negociateurs', 'negociateur'),
    operations: _urlListe('operations', 'operation'),
    objets: _urlListe('objets', 'objet'),
    profils: _urlListe('profils', 'profil'),
    // lot AV10/P3 ter — « Budget max » est l'un des trois filtres permanents, « Budget min »
    // vit dans le panneau (comme Surface, qui a min ET max). Les deux suivent la transaction.
    budgetMin: _urlStr('budgetMin'), budgetMax: _urlStr('budgetMax'),
    surfaceMin: _urlStr('surfaceMin'), surfaceMax: _urlStr('surfaceMax'),
    // 🔴 lot AV10 / P3-5 — « Photos » redevient une rubrique à DEUX valeurs : « Avec photos »
    // sert à choisir ce qu'on envoie à un client, « Sans photo » à repérer les fiches à
    // compléter. La case à cocher unique est supprimée (elle retirait un usage réel).
    photos: _urlStr('sansPhoto') === '1' ? ['sans'] : _urlListe('photos'),
  }));
  const [panneauFacettes, setPanneauFacettes] = useState(false);
  
  // Données dynamiques (depuis /api/properties/criteria)
  const [availableNegotiators, setAvailableNegotiators] = useState([]); // [{id, name}]
  const [availableOperations, setAvailableOperations] = useState([]); // [{value, label}]
  const [availableObjets, setAvailableObjets] = useState([]); // [{value, label}]
  const [availableProfils, setAvailableProfils] = useState([]); // [{value, label}]
  
  // Actions
  const [showActionsDropdown, setShowActionsDropdown] = useState(false);
  const [expandedShareProperty, setExpandedShareProperty] = useState(null);
  const [propertyLinks, setPropertyLinks] = useState({});
  
  // Modale affectation client
  const [showAffectClientModal, setShowAffectClientModal] = useState(false);
  const [propertiesToAffect, setPropertiesToAffect] = useState([]);
  
  // État pour la génération PDF
  const [pdfMenuOpen, setPdfMenuOpen] = useState(null);
  const [generatingPDF, setGeneratingPDF] = useState(null);
  const [bulkPdfProgress, setBulkPdfProgress] = useState({ active: false, current: 0, total: 0, errors: [] });

  // lot AV1/AV4 — { "<id du bien>": { "<support>": "<état>" } } et supports activés de l'agence.
  const [etatsDiffusion, setEtatsDiffusion] = useState({});
  const [diffusionConnue, setDiffusionConnue] = useState(false);
  // lot BY — supprimer un bien et voir l'onglet « Supprimés » sont réservés à l'admin.
  const estAdmin = useMemo(() => {
    try {
      const role = JSON.parse(localStorage.getItem('user') || '{}')?.role;
      return role === 'admin_agence' || role === 'super_admin';
    } catch { return false; }
  }, []);
  const [supportsDiffusion, setSupportsDiffusion] = useState([]);
  const [lotDiffusion, setLotDiffusion] = useState(null);   // { action, biens }
  // lot CR — un SEUL bien ouvre « Gérer la diffusion » (la même fenêtre que la fiche) :
  // l'envoi groupé ne permet pas de couper un portail.
  const [bienDiffusion, setBienDiffusion] = useState(null);

  // lot CR2 — la projection de `/properties/list` ne contient pas `diffusion` : sans le bien
  // complet, tous les supports s'affichaient « Non diffusé » et un clic partait d'un état faux.
  const ouvrirGestionDiffusion = async (property) => {
    try {
      const { data } = await api.get(`/properties/${property.id}`);
      setBienDiffusion(data);
    } catch (error) {
      toast.error(messageServeur(error, 'Erreur chargement du bien'));
    }
  };
  const [feuilleActionsOuverte, setFeuilleActionsOuverte] = useState(false);
  const [menuLotOuvert, setMenuLotOuvert] = useState(false);

  // === CHARGEMENT INITIAL ===
  const loadedRef = useRef(false);
  const favoriteTogglingRef = useRef(false); // Pour éviter les doubles clics

  useEffect(() => {
    // ⚠️ UN SEUL chargement initial. Auparavant deux useEffect lançaient chacun
    // loadProperties + loadCounts au montage → 2 requêtes /api/properties (150 Ko)
    // et 2 /counts en double, d'où la lenteur du premier affichage (iter127).
    if (loadedRef.current) return;
    loadedRef.current = true;
    loadFavorites();
    // lot AV1 — l'état de diffusion est chargé EN PARALLÈLE et n'est PAS attendu : la liste
    // s'affiche sans lui, les étiquettes apparaissent quand la réponse arrive. ⛔ Il n'entre
    // pas dans le `Promise.all` du chargement, sinon le premier affichage attendrait.
    chargerEtatsDiffusion();
    const status = searchParams.get('filter') || currentFilter;
    setLoading(true);
    Promise.all([
      loadProperties(status, false),
      loadCounts(),
      loadCriteria(),
    ])
      .catch(() => setError('Erreur lors du chargement des données'))
      .finally(() => setLoading(false));
  }, []);
  
  // Écouter les changements de filtre dans l'URL
  useEffect(() => {
    const filterFromUrl = searchParams.get('filter');
    if (filterFromUrl && filterFromUrl !== currentFilter) {
      loadProperties(filterFromUrl, true); // Force refresh
    }
  }, [searchParams]);

  // lot AE3 — recherche du header : le lien « Voir les N biens » arrive sur /properties?reference=…
  // On relit la clé à chaque NAVIGATION (location.key) et non à chaque rendu : lire `searchParams`
  // en continu écraserait la frappe en cours, puisque l'URL est réécrite depuis les filtres.
  useEffect(() => {
    // Ignorer les changements d'adresse que nous venons d'écrire nous-mêmes.
    if (searchParams.toString() === derniereUrlEcrite.current) return;
    const refUrl = searchParams.get('reference') || '';
    setSearchFilters(prev => (prev.reference === refUrl ? prev : { ...prev, reference: refUrl }));
  }, [location.key]);

  // lot V1 — miroir état → adresse. `replace` et non `push` : sinon chaque frappe clavier
  // empilerait une entrée d'historique et le bouton retour deviendrait inutilisable.
  // Les paramètres vides ne sont pas écrits : sans filtre, l'adresse reste /properties.
  useEffect(() => {
    const p = new URLSearchParams();
    if (currentFilter && currentFilter !== 'active') p.set('filter', currentFilter);
    const simples = {
      reference: searchFilters.reference,
      budgetMin: searchFilters.budgetMin, budgetMax: searchFilters.budgetMax,
      surfaceMin: searchFilters.surfaceMin, surfaceMax: searchFilters.surfaceMax,
    };
    Object.entries(simples).forEach(([k, v]) => { if (v) p.set(k, String(v)); });
    // lot AV10 — format pluriel UNIQUEMENT en écriture (les singuliers restent lus au montage).
    ['villes', 'negociateurs', 'operations', 'objets', 'profils', 'photos'].forEach((cle) => {
      if (searchFilters[cle]?.length) p.set(cle, searchFilters[cle].join(','));
    });

    if (currentPage > 1) p.set('page', String(currentPage));
    if (itemsPerPage !== 20) p.set('taille', String(itemsPerPage));
    if (sortColumn !== 'created_at') p.set('tri', sortColumn);
    if (sortOrder !== 'desc') p.set('ordre', sortOrder);
    // lot CE — mémoriser l'adresse de la liste pour la flèche de retour des fiches. Écrit à
    // chaque passage (même sans changement d'adresse), sinon un retour depuis une fiche
    // ouverte sans nouvelle frappe repartirait sur une adresse périmée.
    try {
      const q = p.toString();
      sessionStorage.setItem('komerces:retourListe', '/properties' + (q ? '?' + q : ''));
    } catch { /* stockage indisponible : la flèche retombera sur la liste complète */ }
    if (p.toString() !== searchParams.toString()) {
      // lot AE3 — on note l'adresse que NOUS écrivons : l'effet de lecture ci-dessus doit
      // pouvoir distinguer cette écriture d'une vraie navigation (sinon les deux effets se
      // réalimentent : `replace` change aussi location.key → oscillation sans fin).
      derniereUrlEcrite.current = p.toString();
      setSearchParams(p, { replace: true });
    }
  }, [searchFilters, currentPage, itemsPerPage, sortColumn, sortOrder, currentFilter, searchParams]);

  const loadData = async () => {
    setLoading(true);
    // lot AV1 — l'état de diffusion est chargé EN PARALLÈLE, jamais attendu : la liste
    // s'affiche sans lui, les étiquettes apparaissent quand la réponse arrive. ⛔ Aucune
    // cascade, aucun écran bloqué, et un appel UNIQUE pour tout le portefeuille.
    chargerEtatsDiffusion();
    try {
      await Promise.all([
        loadProperties(currentFilter, false), // false = utiliser cache si disponible
        loadCounts(),
        loadCriteria()
      ]);
    } catch (err) {
      setError('Erreur lors du chargement des données');
    } finally {
      setLoading(false);
    }
  };

  // lot AV1/AV3 — état de diffusion par bien, en un appel, hors du chemin critique.
  const chargerEtatsDiffusion = async () => {
    try {
      const { data } = await api.get('/properties/diffusion-states');
      setEtatsDiffusion(data || {});
      // lot BX — 🔴 un bien ABSENT de cette réponse n'est diffusé nulle part : c'est une
      // information. Sans ce drapeau, la confirmation de suppression basculait sur « Si ce
      // bien est diffusé… » pour un bien dont on sait parfaitement qu'il ne l'est pas.
      setDiffusionConnue(true);
    } catch (e) {
      // Un état de diffusion indisponible ne doit RIEN afficher de faux : on n'invente pas
      // « non diffusé », on laisse les étiquettes absentes.
      console.warn('[AV1] états de diffusion indisponibles', e?.message);
    }
    try {
      const overview = await fetchDiffusionOverview(1, 1);
      setSupportsDiffusion(overview?.supports || []);
    } catch (e) {
      console.warn('[AV1] supports de diffusion indisponibles', e?.message);
    }
  };

  const loadFavorites = () => {
    try {
      const saved = localStorage.getItem('favoriteProperties');
      if (saved) {
        const favs = JSON.parse(saved);
        setLocalFavorites(favs);
        console.log('[loadFavorites] Chargés depuis localStorage:', favs.length);
      }
    } catch (e) {
      console.error('Erreur chargement favoris:', e);
    }
  };

  const loadProperties = async (status = 'active', forceRefresh = false) => {
    try {
      // Vérifier le cache d'abord (sauf si forceRefresh)
      if (!forceRefresh && propertiesCache[status]) {
        console.log(`📦 Cache HIT pour ${status}`);
        setProperties(propertiesCache[status]);
        setCurrentFilter(status);
        return;
      }
      
      console.log(`🔄 Chargement API pour ${status}...`);
      const statusMapping = {
        'archive': 'archived', 'active': 'active', 'draft': 'draft',
        'sold': 'sold', 'all': 'all', 'shared': 'all', 'favorites': 'active',
        'deleted': 'deleted',      // lot BY — onglet « Supprimés »
        // Filtres actionnables du tableau de bord
        'actifs': 'active', 'mandats_expirants_30j': 'active', 'mandats_echus': 'active',
        'dormants_60j': 'active', 'sans_photo': 'active', 'affaires_en_cours': 'all',
        // lot CD — au-delà de 12 mois + qualité des biens
        'mandats_echus_anciens': 'active', 'dormants_anciens': 'active',
        'sans_description': 'active', 'sans_surface': 'active',
        // lot AV4 — un bien « modifié non renvoyé » est forcément en ligne, donc actif.
        'modifies': 'active',
      };
      const actionableFilters = ['mandats_expirants_30j', 'mandats_echus', 'mandats_echus_anciens',
        'dormants_60j', 'dormants_anciens', 'sans_photo', 'sans_description', 'sans_surface',
        'affaires_en_cours'];
      let url = `/properties/list?status=${statusMapping[status] || status}&limit=5000`;
      if (actionableFilters.includes(status)) {
        url += `&actionable=${status}`; // filtrage côté serveur (docs complets)
      }
      // lot AG1 — chargement en DEUX TEMPS. L'attente serveur est proportionnelle au nombre
      // de biens (100 biens ≈ 209 ms, 1 220 biens ≈ 1 210 ms) : on affiche donc d'abord la
      // première page, puis on complète en arrière-plan. La recherche et les filtres du lot AE
      // tournent dans le navigateur et ont besoin de TOUT le portefeuille : le second appel
      // ramène bien l'intégralité (aucune pagination serveur, aucun cache navigateur).
      // lot AL7 — les filtres actionnables (mandats échus, dormants, sans photo…) sont DÉJÀ
      // restreints par le serveur : leur résultat tient largement dans un seul appel. Les
      // charger en deux temps affichait 60 lignes, puis le total réel une seconde plus tard,
      // et les compteurs de l'écran mentaient dans l'intervalle. Un seul appel complet ici.
      const enDeuxTemps = !actionableFilters.includes(status);
      const urlPremierePage = enDeuxTemps
        ? url.replace('&limit=5000', `&limit=${PREMIERE_PAGE}`)
        : url;
      const response = await api.get(urlPremierePage);
      let data = response.data;
      const portefeuilleIncomplet = enDeuxTemps
        && Array.isArray(data) && data.length >= PREMIERE_PAGE;
      
      // Filtres spéciaux client (le reste est filtré côté serveur via &actionable=)
      if (status === 'shared') {
        data = data.filter(p => p.is_public && p.public_code);
      } else if (status === 'favorites') {
        // Recharger les favoris depuis localStorage pour être sûr d'avoir la dernière version
        const saved = localStorage.getItem('favoriteProperties');
        const currentFavs = saved ? JSON.parse(saved) : [];
        console.log('[loadProperties] Favoris pour filtrage:', currentFavs);
        // Filtrer uniquement les biens en favoris
        data = data.filter(p => currentFavs.includes(p.id));
        console.log('[loadProperties] Biens filtrés:', data.length);
      }
      
      setProperties(data);
      setCurrentFilter(status);
      setChargementComplet(!portefeuilleIncomplet);

      // Mettre en cache
      setPropertiesCache(prev => ({ ...prev, [status]: data }));

      // Second temps : le reste du portefeuille, sans bloquer l'affichage.
      // ⚠️ Garde anti-boucle (lot AA) : ce chargement n'est jamais déclenché par un rendu — il
      // est lancé ici, une seule fois par appel de loadProperties, et son résultat ne fait que
      // remplacer `properties`. `chargementEnCoursRef` empêche deux passes concurrentes.
      if (portefeuilleIncomplet && !chargementEnCoursRef.current) {
        chargementEnCoursRef.current = true;
        api.get(url)
          .then(resTotal => {
            let complet = resTotal.data;
            if (status === 'shared') {
              complet = complet.filter(p => p.is_public && p.public_code);
            } else if (status === 'favorites') {
              const saved = localStorage.getItem('favoriteProperties');
              const currentFavs = saved ? JSON.parse(saved) : [];
              complet = complet.filter(p => currentFavs.includes(p.id));
            }
            setProperties(complet);
            setPropertiesCache(prev => ({ ...prev, [status]: complet }));
            setChargementComplet(true);
          })
          .catch(err => {
            console.error('[loadProperties] second chargement échoué:', err);
            setChargementComplet(true);  // ne pas laisser la mention affichée indéfiniment
          })
          .finally(() => { chargementEnCoursRef.current = false; });
      }
      
      // Charger les liens existants
      const links = {};
      data.forEach(p => {
        if (p.public_code) {
          links[p.id] = { code: p.public_code, url: `${window.location.origin}/p/${p.public_code}` };
        }
      });
      setPropertyLinks(links);
    } catch (error) {
      console.error('Erreur chargement biens:', error);
      setError('Impossible de charger les biens');
    }
  };

  const loadCounts = async () => {
    try {
      const response = await api.get(`/properties/counts`);
      setCounts({
        all: response.data.all || 0,
        active: response.data.active || 0,
        draft: response.data.draft || 0,
        archive: response.data.archived || 0,
        sold: response.data.sold || 0,
        shared: response.data.shared || 0,
        deleted: response.data.deleted || 0,      // lot BY — onglet « Supprimés » (admin)
      });
    } catch (error) {
      console.error('Erreur compteurs:', error);
    }
  };

  const loadCriteria = async () => {
    try {
      const response = await api.get(`/properties/criteria`);
      // Tri alphabétique FR global (placeholder haut, "Autre" bas)
      // lot AV10 — `cities` n'est plus consommée : les valeurs de la facette Ville viennent
      // des biens eux-mêmes (une valeur proposée sans aucun bien derrière n'a pas de sens).
      setAvailableNegotiators(sortSelectOptions(response.data.negotiators || []));
      // NB: operations = ordre business (Cession → Vente → Location…) → NON trié
      setAvailableOperations(response.data.operations || []);
      setAvailableObjets(sortSelectOptions(response.data.objets || []));
      setAvailableProfils(sortSelectOptions(response.data.profils || []));
    } catch (error) {
      console.error('Erreur critères:', error);
    }
  };

  // === HANDLERS ===
  const handleFilterChange = (newFilter) => {
    setCurrentFilter(newFilter);
    setSelectedProperties([]);
    navigate(`/properties?filter=${newFilter}`);
    loadProperties(newFilter);
  };

  const handleSelectProperty = (propertyId, checked) => {
    if (checked) {
      setSelectedProperties(prev => [...prev, propertyId]);
    } else {
      setSelectedProperties(prev => prev.filter(id => id !== propertyId));
    }
  };

  const handleSelectAll = (checked) => {
    if (checked) {
      setSelectedProperties(sortedProperties.map(p => p.id));
    } else {
      setSelectedProperties([]);
    }
  };

  const toggleFavorite = async (propertyId) => {
    const timestamp = new Date().toISOString();
    console.log(`🔵 [${timestamp}] toggleFavorite APPELÉE avec ID:`, propertyId);
    
    // Lire directement depuis localStorage pour avoir la valeur la plus récente
    const saved = localStorage.getItem('favoriteProperties');
    const currentFavs = saved ? JSON.parse(saved) : [];
    
    console.log(`📦 [${timestamp}] localStorage AVANT:`, currentFavs);
    console.log(`📊 [${timestamp}] Nombre AVANT:`, currentFavs.length);
    
    // Calculer le nouveau state
    const isCurrentlyFavorite = currentFavs.includes(propertyId);
    const newFavs = isCurrentlyFavorite
      ? currentFavs.filter(id => id !== propertyId)
      : [...currentFavs, propertyId];
    
    console.log(`${isCurrentlyFavorite ? '➖' : '➕'} [${timestamp}] Action: ${isCurrentlyFavorite ? 'RETIRER' : 'AJOUTER'}`);
    console.log(`📦 [${timestamp}] localStorage APRÈS:`, newFavs);
    console.log(`📊 [${timestamp}] Nombre APRÈS:`, newFavs.length);
    
    // Sauvegarder dans localStorage
    localStorage.setItem('favoriteProperties', JSON.stringify(newFavs));
    console.log(`✅ [${timestamp}] SAUVEGARDÉ dans localStorage`);
    
    // Vérifier immédiatement
    const verification = localStorage.getItem('favoriteProperties');
    console.log(`🔍 [${timestamp}] VÉRIFICATION immédiate:`, verification);
    
    // Mettre à jour le state local
    setLocalFavorites(newFavs);
    console.log(`🔄 [${timestamp}] setLocalFavorites appelé`);
    
    // Invalider le cache 'favorites'
    setPropertiesCache(prev => {
      const updated = { ...prev };
      delete updated.favorites;
      return updated;
    });
    
    // Si on est sur le filtre favoris, recharger
    if (currentFilter === 'favorites') {
      setTimeout(() => {
        loadProperties('favorites', true);
      }, 100);
    }
    
    // Notifier le parent
    if (onPropertiesChange) {
      onPropertiesChange(properties, newFavs);
    }
    
    // Callback externe
    if (onToggleFavorite) {
      onToggleFavorite(propertyId);
    }
    
    console.log(`✅ [${timestamp}] toggleFavorite TERMINÉE\n`);
  };

  // === AFFECTATION CLIENT ===
  const handleAffectClient = (propertyId) => {
    setPropertiesToAffect([propertyId]);
    setShowAffectClientModal(true);
  };

  const handleBulkAffectClient = () => {
    if (selectedProperties.length === 0) {
      alert('Veuillez sélectionner au moins un bien');
      return;
    }
    setPropertiesToAffect(selectedProperties);
    setShowAffectClientModal(true);
  };
  
  const handleAffectSuccess = (contactId) => {
    console.log('✅ Biens affectés au contact:', contactId);
    // Désélectionner les biens après succès
    setSelectedProperties([]);
    // Afficher un message de succès
    alert(`${propertiesToAffect.length} bien(s) affecté(s) avec succès au contact !`);
  };
  
  // === GÉNÉRATION PDF ===
  const handleGeneratePDF = async (propertyId, version, e) => {
    if (e) e.stopPropagation();
    if (generatingPDF === propertyId) return;
    
    setGeneratingPDF(propertyId);
    setPdfMenuOpen(null);
    
    try {
      const response = await api.get(`/properties/${propertyId}/fiche-bien`, {
        params: { version },
        responseType: 'blob'
      });
      
      // Trouver le bien pour obtenir la référence
      const prop = properties.find(p => p.id === propertyId);
      const ref = prop?.reference || propertyId?.slice(0, 8);
      const versionLabel = version === 'confidential' ? 'agent' : 'client';
      
      // Créer un lien pour télécharger le fichier
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `fiche_bien_${ref}_${versionLabel}.pdf`);
      
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
      
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Erreur génération PDF:', err);
      alert('Erreur lors de la génération du PDF. Veuillez réessayer.');
    } finally {
      setGeneratingPDF(null);
    }
  };

  // === GÉNÉRATION PDF EN LOT (UN SEUL FICHIER) ===
  const handleBulkGeneratePDF = async (version) => {
    if (selectedProperties.length === 0 || bulkPdfProgress.active) return;
    // lot G — plafond de prévention : au-delà de 5 biens, la génération échoue. On refuse
    // avant l'appel, la route refuse aussi de son côté.
    if (selectedProperties.length > MAX_BIENS_DOSSIER) {
      toast.error(MESSAGE_PLAFOND_DOSSIER);
      return;
    }
    
    setBulkPdfProgress({ active: true, current: 0, total: selectedProperties.length, errors: [] });
    
    try {
      const response = await api.post('/properties/bulk-fiche-bien', 
        { property_ids: selectedProperties },
        { 
          params: { version },
          responseType: 'blob'
        }
      );
      
      const versionLabel = version === 'confidential' ? 'agent' : 'client';
      const filename = `dossier_${selectedProperties.length}_biens_${versionLabel}.pdf`;
      
      // Créer un lien pour télécharger le fichier
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
      
      window.URL.revokeObjectURL(url);
      
    } catch (err) {
      console.error('Erreur génération PDF groupé:', err);
      alert('Erreur lors de la génération du PDF groupé. Veuillez réessayer.');
    } finally {
      setBulkPdfProgress({ active: false, current: 0, total: 0, errors: [] });
    }
  };

  // === HANDLERS DE NAVIGATION ===
  const handleViewProperty = (property) => {
    console.log('👁️ View property:', property.id);
    // NOUVELLE NAVIGATION: Route canonique /properties
    navigate(`/properties/${property.id}/view`);
  };

  const handleEditProperty = (property) => {
    console.log('✏️ Edit property:', property.id);
    // Navigation canonique vers /properties/:id/edit
    navigate(`/properties/${property.id}/edit`);
  };

  const handleNewProperty = () => {
    console.log('➕ New property');
    // Navigation canonique vers /properties/wizard/new
    if (startNewProperty) {
      startNewProperty();
    } else {
      navigate('/properties/wizard/new');
    }
  };

  // === ACTIONS ===
  // lot BX — ces deux actions retirent les annonces des portails : la confirmation doit le dire.
  // `undefined` = l'écran n'a pas pu charger la diffusion → message au conditionnel.
  const etatsDe = (id) => (diffusionConnue ? (etatsDiffusion[id] || {}) : undefined);

  const handleArchive = async (propertyId) => {
    if (!window.confirm(messageArchivage(etatsDe(propertyId)))) return;
    try {
      await api.patch(`/properties/${propertyId}/archive`);
      // Invalider tout le cache
      setPropertiesCache({});
      await Promise.all([loadProperties(currentFilter, true), loadCounts()]);
    } catch (error) {
      alert('Erreur archivage');
    }
  };

  const handleRestore = async (propertyId) => {
    try {
      await api.patch(`/properties/${propertyId}/restore`);
      // Invalider tout le cache
      setPropertiesCache({});
      await Promise.all([loadProperties(currentFilter, true), loadCounts()]);
    } catch (error) {
      toast.error(messageServeur(error, 'Erreur restauration'));
    }
  };

  // lot BY — restauration groupée : on ATTEND chaque appel (sinon les compteurs sont rechargés
  // avant la fin), et on annonce ce qu'on a obtenu — actif ou brouillon.
  const handleBulkRestore = async () => {
    const total = selectedProperties.length;
    if (!total) return;
    if (!window.confirm(
      `Restaurer ${total} bien(s) ? Ils repasseront en actif (en brouillon s'il manque le `
      + 'mandat ou le loyer). Aucune annonce ne sera republiée.')) return;
    let actifs = 0;
    let brouillons = 0;
    let echecs = 0;
    for (const id of selectedProperties) {
      try {
        const { data } = await api.patch(`/properties/${id}/restore`);
        if (data?.status === 'draft') brouillons += 1; else actifs += 1;
      } catch (error) {
        echecs += 1;
        if (echecs === 1) toast.error(messageServeur(error, 'Erreur restauration'));
      }
    }
    setSelectedProperties([]);
    setPropertiesCache({});
    await Promise.all([loadProperties(currentFilter, true), loadCounts()]);
    if (actifs + brouillons) {
      toast.success(`${actifs + brouillons} bien(s) restauré(s) — ${actifs} actif(s), `
        + `${brouillons} brouillon(s).`);
    }
  };

  const handleDelete = async (propertyId) => {
    if (!window.confirm(messageSuppression(etatsDe(propertyId)))) return;
    try {
      await api.delete(`/properties/${propertyId}`);
      // Forcer le rechargement sans cache
      setPropertiesCache({});
      await Promise.all([loadProperties(currentFilter, true), loadCounts()]);
    } catch (error) {
      toast.error(messageServeur(error, 'Erreur suppression'));
    }
  };

  const handleBulkArchive = async () => {
    if (!window.confirm(messageGroupe('archiver', selectedProperties,
                                      diffusionConnue ? etatsDiffusion : undefined))) return;
    try {
      for (const id of selectedProperties) {
        await api.patch(`/properties/${id}/archive`);
      }
      setSelectedProperties([]);
      setPropertiesCache({});
      await Promise.all([loadProperties(currentFilter, true), loadCounts()]);
    } catch (error) {
      alert('Erreur archivage groupé');
    }
  };

  const handleBulkDelete = async () => {
    if (!window.confirm(messageGroupe('supprimer', selectedProperties,
                                      diffusionConnue ? etatsDiffusion : undefined))) return;
    try {
      for (const id of selectedProperties) {
        await api.delete(`/properties/${id}`);
      }
      setSelectedProperties([]);
      setPropertiesCache({});
      await Promise.all([loadProperties(currentFilter, true), loadCounts()]);
    } catch (error) {
      toast.error(messageServeur(error, 'Erreur suppression groupée'));
    }
  };

  // === BULK SHARE : génère les liens publics pour tous les biens sélectionnés
  // et les copie dans le presse-papier ===
  const handleBulkShare = async () => {
    if (selectedProperties.length === 0) return;
    try {
      const results = [];
      for (const id of selectedProperties) {
        const prop = properties.find(p => p.id === id);
        const title = prop ? `${prop.bien_general?.titre_annonce || prop.titre_externe || 'Bien'} (${prop.reference || ''})` : 'Bien';
        const response = await api.post(`/properties/${id}/share`);
        const { share_url } = response.data;
        results.push(`• ${title.trim()} : ${share_url}`);
      }
      const allLinks = results.join('\n');
      // Copier dans le presse-papier
      try {
        await navigator.clipboard.writeText(allLinks);
        alert(`✅ ${results.length} lien${results.length > 1 ? 's' : ''} de partage copié${results.length > 1 ? 's' : ''} dans le presse-papier !\n\nVous pouvez maintenant les coller dans un email ou un message.`);
      } catch {
        // Fallback : afficher dans une prompt
        window.prompt(`${results.length} liens générés (Ctrl+C pour copier) :`, allLinks);
      }
      setSelectedProperties([]);
      await loadProperties(currentFilter, true);
    } catch (error) {
      console.error('Erreur partage groupé:', error);
      alert('Erreur lors du partage groupé');
    }
  };

  // === BULK EMAIL : envoi groupé de fiches via Resend (1 email/destinataire, liens trackés)
  // Bascule sur mailto si l'agence est configurée en `local_mailto` ou si Resend échoue. ===
  const [sendFichesModalOpen, setSendFichesModalOpen] = useState(false);

  const bulkMailtoFallback = async () => {
    const results = [];
    for (const id of selectedProperties) {
      const prop = properties.find(p => p.id === id);
      const title = prop ? `${prop.bien_general?.titre_annonce || prop.titre_externe || 'Bien'} (${prop.reference || ''})` : 'Bien';
      try {
        const response = await api.post(`/properties/${id}/share`);
        results.push(`${title.trim()}\n${response.data.share_url}`);
      } catch (_) { /* ignore */ }
    }
    const subject = encodeURIComponent(`${results.length} bien${results.length > 1 ? 's' : ''} à découvrir`);
    const body = encodeURIComponent(`Bonjour,\n\nVoici une sélection de biens qui pourraient vous intéresser :\n\n${results.join('\n\n')}\n\nN'hésitez pas à me contacter pour plus d'informations.\n\nCordialement`);
    window.location.href = `mailto:?subject=${subject}&body=${body}`;
  };

  const handleBulkEmail = async () => {
    if (selectedProperties.length === 0) return;
    // Détermine la méthode d'envoi de l'agence via le 1er bien sélectionné
    try {
      const resp = await api.post(`/properties/${selectedProperties[0]}/share`);
      const method = resp.data?.email_send_method || 'resend';
      if (method === 'local_mailto') {
        await bulkMailtoFallback();
        setSelectedProperties([]);
        return;
      }
    } catch (_) { /* si échec, on tente quand même le modal Resend */ }
    setSendFichesModalOpen(true);
  };

  const submitBulkFiches = async (recipients, message) => {
    try {
      const resp = await api.post('/properties/send-fiches', {
        property_ids: selectedProperties,
        recipients: recipients.map((r) => ({ email: r.email, contact_id: r.contact_id || null })),
        message,
      });
      const count = resp.data?.count ?? recipients.length;
      const failed = resp.data?.failed || [];
      if (failed.length > 0) {
        toast.warning(`Envoyé à ${count} destinataire(s). Échec : ${failed.join(', ')}`);
      } else {
        toast.success(`${selectedProperties.length} fiche(s) envoyée(s) à ${count} destinataire(s)`);
      }
      setSendFichesModalOpen(false);
      setSelectedProperties([]);
      await loadProperties(currentFilter, true);
    } catch (error) {
      const status = error?.response?.status;
      if (status === 502 || status === 503) {
        toast.info('Envoi automatique indisponible — ouverture de votre messagerie');
        await bulkMailtoFallback();
        setSendFichesModalOpen(false);
        setSelectedProperties([]);
      } else if (status === 401) {
        toast.error('Session expirée — veuillez vous reconnecter');
      } else {
        toast.error(error?.response?.data?.detail || "Échec de l'envoi groupé");
      }
    }
  };

  // === BULK FAVORITES : ajoute/retire tous les biens sélectionnés des favoris ===
  const handleBulkToggleFavorites = () => {
    if (selectedProperties.length === 0) return;
    // Si tous sont déjà en favoris → retirer ; sinon → ajouter
    const allAreFav = selectedProperties.every(id => localFavorites.includes(id));
    let newFavs;
    if (allAreFav) {
      newFavs = localFavorites.filter(id => !selectedProperties.includes(id));
    } else {
      newFavs = Array.from(new Set([...localFavorites, ...selectedProperties]));
    }
    setLocalFavorites(newFavs);
    localStorage.setItem('favoriteProperties', JSON.stringify(newFavs));
    // Invalider le cache "favorites"
    setPropertiesCache(prev => {
      const updated = { ...prev };
      delete updated.favorites;
      return updated;
    });
    if (currentFilter === 'favorites') {
      setTimeout(() => loadProperties('favorites', true), 100);
    }
    const action = allAreFav ? 'retiré(s) des' : 'ajouté(s) aux';
    alert(`✅ ${selectedProperties.length} bien(s) ${action} favoris`);
    setSelectedProperties([]);
  };

  const generatePublicLink = async (propertyId) => {
    try {
      setPropertyLinks(prev => ({ ...prev, [propertyId]: { loading: true } }));
      const response = await api.post(`/properties/${propertyId}/share`);
      const { share_token, share_url, qr_code } = response.data;
      setPropertyLinks(prev => ({
        ...prev,
        [propertyId]: {
          code: share_token,
          url: share_url || `${window.location.origin}/f/${share_token}`,
          qr_code: qr_code || null,
          loading: false
        }
      }));
      setExpandedShareProperty(propertyId);
      await loadProperties(currentFilter, true);
    } catch (error) {
      console.error('Erreur génération lien:', error?.response?.status, error?.response?.data);
      alert('Erreur lors de la génération du lien');
    }
  };

  // Lazy-fetch du QR code pour un bien déjà partagé (n'a pas le QR en state)
  const fetchQrCodeIfNeeded = async (propertyId) => {
    const existing = propertyLinks[propertyId];
    if (existing?.qr_code) return; // déjà chargé
    try {
      const response = await api.post(`/properties/${propertyId}/share`);
      const { share_token, share_url, qr_code } = response.data;
      setPropertyLinks(prev => ({
        ...prev,
        [propertyId]: {
          code: share_token,
          url: share_url || `${window.location.origin}/f/${share_token}`,
          qr_code: qr_code || null,
          loading: false
        }
      }));
    } catch (error) {
      console.error('Erreur fetch QR:', error);
    }
  };

  const disablePublicLink = async (propertyId) => {
    if (!window.confirm('Désactiver le partage de ce bien ?')) return;
    try {
      // Nouvel endpoint DELETE /share (révoque le share_token côté serveur)
      await api.delete(`/properties/${propertyId}/share`);
      // Forcer le rechargement sans cache
      await loadProperties(currentFilter, true);
    } catch (error) {
      alert('Erreur désactivation partage');
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    alert('Lien copié !');
  };

  // === KEBAB DROPDOWN (réutilisable grille + liste) ===
  // direction: 'up' (par défaut, mode grille → s'ouvre vers le haut)
  //            'down' (mode liste → s'ouvre vers le bas pour éviter d'être tronqué en haut)
  const renderKebabDropdown = (property, direction = 'up') => {
    const isFavorite = localFavorites.includes(property.id);
    const positionClass = direction === 'down' ? 'top-full mt-1' : 'bottom-full mb-1';
    const estActif = !property.status || property.status === 'active';

    // lot CC — 🔴 un bien SUPPRIMÉ proposait « Diffuser », les PDF, le partage, les favoris :
    // autant de gestes sur un bien qui n'existe plus pour l'agence. Un seul geste a du sens.
    if (property.status === 'deleted') {
      return (
        <div className={`absolute right-0 ${positionClass} bg-white border border-slate-200 rounded-lg shadow-xl z-[60] min-w-[220px]`}>
          <div className="py-1">
            <button
              onClick={(e) => { e.stopPropagation(); handleRestore(property.id); setActiveDropdown(null); }}
              className="w-full text-left px-4 py-2 text-sm text-slate-700 hover:bg-teal-50 hover:text-teal-700 flex items-center gap-2"
              data-testid={`kebab-restore-${property.id}`}
            >
              <i className="bi bi-arrow-counterclockwise text-teal-500"></i>Restaurer
            </button>
          </div>
        </div>
      );
    }

    return (
      <div className={`absolute right-0 ${positionClass} bg-white border border-slate-200 rounded-lg shadow-xl z-[60] min-w-[220px]`}>
        <div className="py-1">
          {/* lot AV8 — trois groupes séparés, et « Diffuser ce bien » en tête : c'est la
              séparation qui évite le clic de trop.
              lot CC — ⛔ seul un bien ACTIF peut être diffusé (même règle que le serveur). */}
          {estActif && (
            <>
          <button
            onClick={(e) => { e.stopPropagation(); setActiveDropdown(null); ouvrirGestionDiffusion(property); }}
            className="w-full text-left px-4 py-2 text-sm text-slate-700 hover:bg-blue-50 flex items-center gap-2"
            data-testid={`kebab-diffuser-${property.id}`}
          >
            <i className="bi bi-broadcast text-[#28A745]"></i>Diffuser ce bien
          </button>
          <div className="border-t border-slate-100 my-1"></div>
            </>
          )}
          <button
            onClick={(e) => handleGeneratePDF(property.id, 'confidential', e)}
            disabled={generatingPDF === property.id}
            className="w-full text-left px-4 py-2 text-sm text-slate-700 hover:bg-red-50 flex items-center gap-2 disabled:opacity-50"
            data-testid={`kebab-pdf-agent-${property.id}`}
          >
            {generatingPDF === property.id ? (
              <i className="bi bi-hourglass-split text-red-500 animate-spin"></i>
            ) : (
              <i className="bi bi-file-earmark-lock text-red-600"></i>
            )}
            <div>
              <span className="font-medium">PDF Fiche Agent</span>
              <p className="text-xs text-slate-400">Avec confidentiel</p>
            </div>
          </button>
          <button
            onClick={(e) => handleGeneratePDF(property.id, 'public', e)}
            disabled={generatingPDF === property.id}
            className="w-full text-left px-4 py-2 text-sm text-slate-700 hover:bg-green-50 flex items-center gap-2 disabled:opacity-50"
            data-testid={`kebab-pdf-client-${property.id}`}
          >
            {generatingPDF === property.id ? (
              <i className="bi bi-hourglass-split text-green-600 animate-spin"></i>
            ) : (
              <i className="bi bi-file-earmark-text text-green-600"></i>
            )}
            <div>
              <span className="font-medium">PDF Fiche Client</span>
              <p className="text-xs text-slate-400">Sans confidentiel</p>
            </div>
          </button>
          <div className="border-t border-slate-100 my-1"></div>
          <button
            onClick={async (e) => {
              e.stopPropagation();
              setActiveDropdown(null);
              if (property.share_token || property.is_public) {
                if (expandedShareProperty === property.id) {
                  setExpandedShareProperty(null);
                } else {
                  setExpandedShareProperty(property.id);
                  // Lazy-fetch du QR si pas encore chargé pour ce bien
                  fetchQrCodeIfNeeded(property.id);
                }
              } else {
                await generatePublicLink(property.id);
              }
            }}
            className="w-full text-left px-4 py-2 text-sm text-slate-700 hover:bg-blue-50 hover:text-blue-700 flex items-center gap-2"
            data-testid={`kebab-share-${property.id}`}
          >
            <i className="bi bi-link-45deg text-blue-500"></i>
            {(property.share_token || property.is_public) ? 'Voir le lien' : 'Partager'}
          </button>
          {(property.share_token || property.is_public) && (
            <button
              onClick={(e) => { e.stopPropagation(); disablePublicLink(property.id); setActiveDropdown(null); }}
              className="w-full text-left px-4 py-2 text-sm text-slate-700 hover:bg-red-50 hover:text-red-700 flex items-center gap-2"
              data-testid={`kebab-share-disable-${property.id}`}
            >
              <i className="bi bi-link-45deg text-red-500"></i>Désactiver partage
            </button>
          )}
          <div className="border-t border-slate-100 my-1"></div>
          {/* Groupe 2 — relation client */}
          <button
            onClick={(e) => { e.stopPropagation(); toggleFavorite(property.id); setActiveDropdown(null); }}
            className="w-full text-left px-4 py-2 text-sm text-slate-700 hover:bg-pink-50 hover:text-pink-700 flex items-center gap-2"
            data-testid={`kebab-favorite-${property.id}`}
          >
            <i className={`bi ${isFavorite ? 'bi-heart-fill text-red-500' : 'bi-heart text-pink-500'}`}></i>
            {isFavorite ? 'Retirer des favoris' : 'Ajouter aux favoris'}
          </button>
          <button
            onClick={(e) => { e.stopPropagation(); handleAffectClient(property.id); setActiveDropdown(null); }}
            className="w-full text-left px-4 py-2 text-sm text-slate-700 hover:bg-blue-50 hover:text-blue-700 flex items-center gap-2"
            data-testid={`kebab-affect-${property.id}`}
          >
            <i className="bi bi-person-check text-blue-500"></i>Affecter client
          </button>
          {/* Groupe 3 — sortie (ambre puis rouge, en dernier, séparés) */}
          <div className="border-t border-slate-100 my-1"></div>
          {property.status === 'archived' ? (
            <button
              onClick={(e) => { e.stopPropagation(); handleRestore(property.id); setActiveDropdown(null); }}
              className="w-full text-left px-4 py-2 text-sm text-slate-700 hover:bg-teal-50 hover:text-teal-700 flex items-center gap-2"
              data-testid={`kebab-restore-${property.id}`}
            >
              <i className="bi bi-arrow-counterclockwise text-teal-500"></i>Restaurer
            </button>
          ) : (
            <button
              onClick={(e) => { e.stopPropagation(); handleArchive(property.id); setActiveDropdown(null); }}
              className="w-full text-left px-4 py-2 text-sm text-slate-700 hover:bg-amber-50 hover:text-amber-700 flex items-center gap-2"
              data-testid={`kebab-archive-${property.id}`}
            >
              <i className="bi bi-archive text-amber-500"></i>Archiver
            </button>
          )}
          {/* lot BY — 🔴 « Supprimer » n'apparaît que pour un ADMIN et un bien DÉJÀ ARCHIVÉ :
              un bouton grisé invite à chercher pourquoi, un bouton absent ne trompe personne. */}
          {estAdmin && property.status === 'archived' && (
            <button
              onClick={(e) => { e.stopPropagation(); handleDelete(property.id); setActiveDropdown(null); }}
              className="w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-red-50 flex items-center gap-2"
              data-testid={`kebab-delete-${property.id}`}
            >
              <i className="bi bi-trash"></i>Supprimer
            </button>
          )}
        </div>
      </div>
    );
  };

  // === FILTRAGE ET TRI ===
  // Récupère l'identifiant stable du négociateur pour le matching (UUID si dispo, sinon nom)
  // Le champ `negociateur_id` est le plus fiable (toujours rempli par le wizard) — on le priorise.
  // Fallback sur `negociateur` qui peut être : string UUID, objet {id/_id/nom} ou {negociateur: uuid}.
  const getNegociateurKey = (property) => {
    // Délègue au helper unique (voir utils/negociateur.js). La seconde implémentation de
    // getNegociateurName qui vivait ici était MORTE (jamais appelée) et renvoyait toujours
    // une chaîne vide : elle lisait `nom`/`name`/`prenom` sur un objet qui ne porte que
    // la clé `negociateur`, et n'a jamais consulté `negociateur_id`.
    const identifiant = getNegociateurId(property);
    if (identifiant) return identifiant;
    const neg = property.negociateur || property.responsable_dossier;
    if (!neg) return '';
    if (typeof neg === 'string') return neg;
    if (typeof neg === 'object') {
      return neg.id || neg._id || neg.negociateur || neg.nom || neg.name || neg.prenom || '';
    }
    return '';
  };

  // lot AV4/AV7 — ⛔ jamais d'envoi direct : on passe toujours par le récapitulatif.
  const ouvrirLotDiffusion = (action, biens) => {
    if (!biens || biens.length === 0) return;
    let cibles = action === 'update'
      // ⚠️ L'action ne porte que sur les biens modifiés de la sélection.
      ? biens.filter(b => aUnSupportModifie(etatsDiffusion[b.id]))
      : biens;
    // lot CC — ⛔ le serveur refuse un bien non actif : autant ne pas l'envoyer, et le DIRE.
    // Sans ce comptage, l'utilisateur voyait une fenêtre de diffusion se solder par des refus.
    let ignores = 0;
    if (action === 'activate') {
      const actifs = cibles.filter(b => !b.status || b.status === 'active');
      ignores = cibles.length - actifs.length;
      cibles = actifs;
    }
    if (cibles.length === 0) {
      toast.info(ignores > 0
        ? `Aucun bien actif dans la sélection — ${ignores} bien(s) non actif(s) ignoré(s). `
          + 'Seuls les biens actifs peuvent être diffusés.'
        : 'Aucun bien modifié dans la sélection');
      return;
    }
    if (ignores > 0) {
      toast.info(`${ignores} bien(s) non actif(s) ignoré(s) — seuls les biens actifs peuvent `
        + 'être diffusés.');
    }
    setLotDiffusion({
      action,
      biens: cibles.map(b => ({
        id: b.id,
        reference: b.reference || b.id,
        titre: getPropertyListDisplayData(b)?.typeBien || '',
        // lot BP — l'état de diffusion est DÉJÀ chargé (`/properties/diffusion-states`) : la
        // fenêtre peut dire ce qui est déjà en ligne sans un seul appel de plus.
        diffusion: etatsDiffusion[b.id] || {},
      })),
    });
  };

  const handleSort = (column) => {
    if (sortColumn === column) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(column);
      setSortOrder('asc');
    }
  };

  const sortProperties = (propsArray) => {
    return [...propsArray].sort((a, b) => {
      let aValue, bValue;
      
      switch (sortColumn) {
        case 'reference':
          // lot AG3 — trier sur la référence AFFICHÉE (l'UUID donnait un ordre incohérent
          // avec la colonne). Les biens sans référence sont groupés en tête en ordre croissant
          // (chaîne vide) et en fin en ordre décroissant.

          aValue = a.reference || '';
          bValue = b.reference || '';
          break;
        case 'type':
          aValue = a.objet || a.taxonomy?.objet || a.bien_general?.type_bien || '';
          bValue = b.objet || b.taxonomy?.objet || b.bien_general?.type_bien || '';
          break;
        case 'ville':
          aValue = a.informations_confidentielles?.ville || a.localisation?.ville || '';
          bValue = b.informations_confidentielles?.ville || b.localisation?.ville || '';
          break;
        case 'prix':
          // Support location_droit_entree : trier par loyer_mensuel (prix principal)
          aValue = a.honoraires_calcul?.prix_vente || a.informations_vente?.prix_cession || a.informations_bail?.loyer_mensuel || 0;
          bValue = b.honoraires_calcul?.prix_vente || b.informations_vente?.prix_cession || b.informations_bail?.loyer_mensuel || 0;
          break;
        case 'statut':
          aValue = a.status || '';
          bValue = b.status || '';
          break;
        case 'created_at':
          aValue = new Date(a.created_at || 0);
          bValue = new Date(b.created_at || 0);
          break;
        case 'updated_at':
          aValue = new Date(a.updated_at || 0);
          bValue = new Date(b.updated_at || 0);
          break;
        default:
          aValue = new Date(a.created_at || 0);
          bValue = new Date(b.created_at || 0);
      }
      
      if (typeof aValue === 'string') {
        aValue = aValue.toLowerCase();
        bValue = bValue.toLowerCase();
      }
      
      if (sortOrder === 'asc') {
        return aValue > bValue ? 1 : aValue < bValue ? -1 : 0;
      } else {
        return aValue < bValue ? 1 : aValue > bValue ? -1 : 0;
      }
    });
  };

  // lot AF2 — ce calcul parcourait les 1 186 biens à CHAQUE rendu (ouverture d'un menu,
  // survol, changement de page). Mémoïsé sur ses vraies dépendances.
  // Dépendances stables : `properties` (état, nouvelle référence seulement au rechargement),
  // `searchFilters` (état, remplacé uniquement par setSearchFilters), `sortColumn`/`sortOrder`
  // (chaînes). Aucun objet n'est recréé pendant le rendu :
  // c'est ce piège qui avait produit la boucle de requêtes des logos d'agence (lot AA).
  // lot AF3 — les codes de taxonomie correspondant à la saisie sont calculés UNE fois ici,
  // et non une fois par bien (445 nœuds × 1 186 biens à chaque caractère tapé).
  const codesTaxoSaisie = useMemo(
    () => codesTaxonomiePourSaisie(searchFilters.reference || ''),
    [searchFilters.reference],
  );

  // lot AV10 — moteur de recherche à facettes, entièrement côté écran : aucun appel serveur,
  // tout est compté sur le portefeuille DÉJÀ chargé (chargement progressif du lot AG conservé).
  //
  // 🔴 Règle « n−1 » — le compteur d'une rubrique ignore la sélection de CETTE rubrique :
  // cocher Lyon ne met donc jamais Marseille à 0, et les deux se cochent. Une facette est
  // cumulative, ce n'est pas un menu à choix unique.
  //
  // ⛔ Deux extractions de la même valeur (une pour filtrer, une pour compter) divergeraient :
  // `valeursFacettes` est la SEULE lecture, partagée par le filtrage et le comptage.
  const valeursFacettes = (property) => {
    const infoConf = property.informations_confidentielles || {};
    return {
      villes: property.bien_general?.ville_generale
        || property.localisation?.ville
        || infoConf.ville
        || (typeof infoConf.adresse === 'object' ? infoConf.adresse?.ville : '')
        || '',
      negociateurs: getNegociateurKey(property) || '',
      operations: property.operation || property.taxonomy?.operation || '',
      objets: property.objet || property.taxonomy?.objet || '',
      profils: property.profil || property.taxonomy?.profil || '',
      photos: nbPhotosBien(property) > 0 ? 'avec' : 'sans',
    };
  };

  // 🔴 lot AV10/P3 bis — le champ de budget suit la transaction : un prix de vente fait six
  // chiffres, un loyer en fait quatre. Sans transaction choisie (ou avec un MÉLANGE
  // vente + location), le champ est grisé : ⛔ pas de champ actif qui donnerait un résultat faux.
  const budgetNature = useMemo(() => {
    const ops = searchFilters.operations || [];
    if (ops.length === 0) return null;
    if (ops.every(o => OPS_LOCATION.includes(o))) return 'location';
    if (ops.every(o => !OPS_LOCATION.includes(o))) return 'vente';
    return null;
  }, [searchFilters.operations]);

  const moteurFacettes = useMemo(() => {
    const selections = {
      villes: searchFilters.villes || [],
      negociateurs: searchFilters.negociateurs || [],
      operations: searchFilters.operations || [],
      objets: searchFilters.objets || [],
      profils: searchFilters.profils || [],
      photos: searchFilters.photos || [],
    };
    const compteurs = { villes: {}, negociateurs: {}, operations: {}, objets: {}, profils: {}, photos: {} };
    const resultats = [];
    const presqueBudget = [];

    const plafond = budgetNature && searchFilters.budgetMax ? parseFloat(searchFilters.budgetMax) : null;
    const plancher = budgetNature && searchFilters.budgetMin ? parseFloat(searchFilters.budgetMin) : null;
    // Un acquéreur ne doit pas rater un bien à quelques pour cent au-dessus de son budget,
    // mais ⛔ élargir le filtre en silence serait l'écran qui décide à sa place : les biens
    // entre le plafond et +10 % vont dans un bloc SÉPARÉ, sous la liste.
    // ⚠️ En location, le champ s'intitule « Loyer annuel » : on compare donc 12 mois de loyer.
    const montantDuBien = (property) => (budgetNature === 'location'
      ? (getLoyerMensuel(property) || 0) * 12
      : (property.prix
        || property.market?.asking_price
        || property.informations_vente?.prix_cession
        || property.informations_vente?.prix_annonce
        || property.honoraires_calcul?.prix_vente || 0));

    // Socle commun : onglet, recherche textuelle, surface. ⚠️ Ce ne sont PAS des facettes
    // (rien à dénombrer par valeur) : elles entrent dans le socle, donc dans tous les compteurs.
    const passeSocle = (property) => {
      // lot AV4 — onglet « Modifiés non renvoyés » : au moins un support en ligne dont le
      // message a changé depuis le dernier envoi accepté (indicateur stocké, lot AV2).
      if (currentFilter === 'modifies' && !aUnSupportModifie(etatsDiffusion[property.id])) return false;
      // lot AE1/AE2 — règle unique : utils/propertySearch.js (la taxonomie se cherche par
      // ses LIBELLÉS : « licence IV », « boutique », « CHR »).
      if (searchFilters.reference
          && !propertyMatchesQuery(property, searchFilters.reference, codesTaxoSaisie)) return false;

      const surface = property.surface_totale_m2
        || property.composition?.superficie_totale
        || property.composition?.surface_totale
        || property.composition?.surface_locaux_principaux || 0;
      if (searchFilters.surfaceMin && surface < parseFloat(searchFilters.surfaceMin)) return false;
      if (searchFilters.surfaceMax && surface > parseFloat(searchFilters.surfaceMax)) return false;

      return true;
    };

    for (const property of properties) {
      const v = valeursFacettes(property);
      const echecs = CLES_FACETTES.filter(
        cle => selections[cle].length > 0 && !selections[cle].includes(v[cle]));
      const montant = (plafond !== null || plancher !== null) ? montantDuBien(property) : 0;
      // ⚠️ Le plancher est un filtre franc (aucune marge) : la marge de 10 % ne concerne que
      // le plafond — c'est le dépassement de budget qui fait rater un bien, pas l'inverse.
      const dansLeBudget = (plafond === null || montant <= plafond) && (plancher === null || montant >= plancher);
      const legerementAuDessus = plafond !== null && montant > plafond && montant <= plafond * MARGE_BUDGET
        && (plancher === null || montant >= plancher);

      if (passeSocle(property)) {
        if (echecs.length === 0 && dansLeBudget) resultats.push(property);
        // n−1 : un bien alimente le compteur d'une rubrique dès que toutes les AUTRES passent.
        if (echecs.length <= 1) {
          for (const cle of CLES_FACETTES) {
            if (echecs.length === 1 && echecs[0] !== cle) continue;
            // 🔴 Le budget ne contraint PAS les compteurs de « Transaction » : son sens DÉRIVE
            // de la transaction. Sinon, un budget de vente ferait tomber les locations à 0,
            // elles disparaîtraient de la rubrique, et l'utilisateur serait enfermé dans la
            // vente sans pouvoir en sortir. Même esprit que la règle n−1.
            if (cle !== 'operations' && !dansLeBudget) continue;
            const valeur = v[cle];
            if (!valeur) continue;
            compteurs[cle][valeur] = (compteurs[cle][valeur] || 0) + 1;
          }
        }
      }
      if (echecs.length === 0 && legerementAuDessus && passeSocle(property)) {
        presqueBudget.push(property);
      }
    }
    return { resultats, compteurs, presqueBudget };
  }, [properties, searchFilters, codesTaxoSaisie, currentFilter, etatsDiffusion, budgetNature]);

  const sortedAndFilteredProperties = useMemo(
    () => sortProperties(moteurFacettes.resultats),
    [moteurFacettes, sortColumn, sortOrder]);

  // Libellés des valeurs : ils viennent des critères de l'agence (`/properties/criteria`),
  // avec en second recours les tables partagées `utils/propertyKpis.js` (les codes hérités
  // `vente_fonds` / `vente_murs` ne sont pas dans les critères et sortaient en clair).
  // Une valeur inconnue des deux garde sa valeur brute — on n'invente aucun libellé.
  const libellesFacettes = useMemo(() => ({
    villes: {},
    negociateurs: Object.fromEntries(availableNegotiators.map(n => [n.id, n.name])),
    operations: { ...OPERATION_LABELS, ...Object.fromEntries(availableOperations.map(o => [o.value, o.label])) },
    objets: Object.fromEntries(availableObjets.map(o => [o.value, o.label])),
    profils: { ...PROFIL_LABELS, ...Object.fromEntries(availableProfils.map(o => [o.value, o.label])) },
    photos: { avec: 'Avec photos', sans: 'Sans photo' },
  }), [availableNegotiators, availableOperations, availableObjets, availableProfils]);

  // 🔴 Une valeur COCHÉE reste proposée même à (0) et reste décochable : l'écran ne défait
  // jamais une sélection de l'utilisateur. Une valeur non cochée tombée à 0 disparaît.
  //
  // 🔴 Regroupement par LIBELLÉ (règle générique, toutes rubriques) : deux codes qui portent le
  // même libellé ne font qu'UNE ligne, dont le compteur est la somme et dont le cochage
  // sélectionne les deux codes. Deux lignes identiques feraient rater en silence les biens de
  // l'autre code. ⛔ Rien n'est fusionné en base ni dans l'adresse : les codes restent distincts.
  // 🔴 Nombre de valeurs DISTINCTES dans le portefeuille complet (aucun filtre appliqué) : c'est
  // lui, et lui seul, qui décide si une rubrique s'affiche. Une rubrique présente au chargement
  // ne disparaît plus jamais de la barre — l'écran ne retire pas les commandes dont
  // l'utilisateur a besoin pour élargir une recherche trop étroite.
  const valeursPortefeuille = useMemo(() => {
    const parRubrique = Object.fromEntries(CLES_FACETTES.map(cle => [cle, new Set()]));
    for (const property of properties) {
      const v = valeursFacettes(property);
      for (const cle of CLES_FACETTES) {
        if (v[cle]) parRubrique[cle].add(libellesFacettes[cle][v[cle]] || v[cle]);
      }
    }
    return Object.fromEntries(CLES_FACETTES.map(cle => [cle, parRubrique[cle].size]));
  }, [properties, libellesFacettes]);

  const facettes = useMemo(() => TITRES_FACETTES.map(({ cle, titre, vide, accord, rapide }) => {
    const selection = searchFilters[cle] || [];
    const compteurs = moteurFacettes.compteurs[cle];
    const valeurs = new Set([...Object.keys(compteurs).filter(v => compteurs[v] > 0), ...selection]);
    const parLibelle = new Map();
    for (const code of valeurs) {
      const label = libellesFacettes[cle][code] || code;
      const ligne = parLibelle.get(label) || { label, codes: [], count: 0 };
      ligne.codes.push(code);
      ligne.count += compteurs[code] || 0;
      parLibelle.set(label, ligne);
    }
    const options = [...parLibelle.values()]
      .map(l => ({ ...l, codes: [...l.codes].sort(), value: [...l.codes].sort().join('+') }))
      .sort((a, b) => String(a.label).localeCompare(String(b.label), 'fr'));
    return {
      cle, titre, vide, accord, rapide, selection, options,
      nbValeursPortefeuille: valeursPortefeuille[cle] || 0,
    };
  }), [moteurFacettes, libellesFacettes, searchFilters, valeursPortefeuille]);

  // Relevé des libellés regroupés — il sert à commander la migration de données.
  const libellesRegroupes = useMemo(
    () => facettes.flatMap(f => f.options
      .filter(o => o.codes.length > 1)
      .map(o => `${f.titre} · « ${o.label} » = ${o.codes.join(' + ')} (${o.count})`)),
    [facettes]);

  // Cocher/décocher une LIGNE : tous ses codes basculent ensemble.
  const basculerFacette = (cle, codes) => {
    const liste = Array.isArray(codes) ? codes : [codes];
    setSearchFilters(p => {
      const actuel = p[cle] || [];
      const dejaCoche = liste.some(c => actuel.includes(c));
      return {
        ...p,
        [cle]: dejaCoche ? actuel.filter(v => !liste.includes(v)) : [...actuel, ...liste.filter(c => !actuel.includes(c))],
      };
    });
    setCurrentPage(1);
  };

  const reinitialiserFiltres = () => {
    setSearchFilters({
      reference: '', villes: [], negociateurs: [], operations: [], objets: [], profils: [], photos: [],
      budgetMin: '', budgetMax: '', surfaceMin: '', surfaceMax: '',
    });
    setCurrentPage(1);
  };

  // 🔴 Un budget devenu inopérant (aucune transaction, ou mélange vente + location) est EFFACÉ :
  // valeur du champ, compteur « (n) », pastille ET adresse. L'écran ne doit pas affirmer un
  // filtre qui n'agit pas, et une adresse partagée ne doit pas porter un budget fantôme.
  // ⛔ On ne le mémorise pas pour le restituer : un filtre qui se réapplique tout seul, c'est
  // l'écran qui décide à la place de l'utilisateur.
  useEffect(() => {
    if (budgetNature) return;
    if (!searchFilters.budgetMin && !searchFilters.budgetMax) return;
    setSearchFilters(p => ({ ...p, budgetMin: '', budgetMax: '' }));
  }, [budgetNature, searchFilters.budgetMin, searchFilters.budgetMax]);

  // Pastilles : tout filtre actif est visible panneau fermé, et compté dans le « (n) » du
  // bouton — un filtre actif invisible est le défaut d'AV1-bis sous une autre forme.
  const pastilles = useMemo(() => {
    const liste = [];
    for (const f of facettes) {
      // Une ligne regroupée = UNE pastille (elle retire ses deux codes d'un coup).
      for (const o of f.options.filter(opt => opt.codes.some(c => f.selection.includes(c)))) {
        liste.push({
          id: `${f.cle}-${o.value}`,
          libelle: `${f.titre} : ${o.label}`,
          retirer: () => basculerFacette(f.cle, o.codes),
        });
      }
    }
    // ⚠️ Vocabulaire de la production : « Prix FAI » en vente, « Loyer annuel » en location.
    if (budgetNature && (searchFilters.budgetMin || searchFilters.budgetMax)) {
      const quoi = budgetNature === 'location' ? 'Loyer annuel' : 'Prix FAI';
      const suffixe = budgetNature === 'location' ? ' / an' : '';
      liste.push({
        id: 'budget',
        libelle: `${quoi} : ${searchFilters.budgetMin ? formatPriceFR(searchFilters.budgetMin) : '0 €'} – ${searchFilters.budgetMax ? formatPriceFR(searchFilters.budgetMax) : '∞'}${suffixe}`,
        retirer: () => setSearchFilters(p => ({ ...p, budgetMin: '', budgetMax: '' })),
      });
    }
    if (searchFilters.surfaceMin || searchFilters.surfaceMax) {
      liste.push({
        id: 'surface',
        libelle: `Surface : ${searchFilters.surfaceMin ? formatSurfaceFR(searchFilters.surfaceMin) : '0 m²'} – ${searchFilters.surfaceMax ? formatSurfaceFR(searchFilters.surfaceMax) : '∞'}`,
        retirer: () => setSearchFilters(p => ({ ...p, surfaceMin: '', surfaceMax: '' })),
      });
    }
    return liste;
  }, [facettes, libellesFacettes, searchFilters, budgetNature]);

  // ⚠️ Le « (n) » du bouton ne compte QUE les rubriques du panneau (une par rubrique, pas une
  // par valeur) : Ville, Type de bien et Transaction sont sur la ligne, donc déjà visibles.
  const nbFiltresPanneau = useMemo(() => {
    let n = facettes.filter(f => !f.rapide && f.selection.length > 0).length;
    if (budgetNature && (searchFilters.budgetMin || searchFilters.budgetMax)) n += 1;
    if (searchFilters.surfaceMin || searchFilters.surfaceMax) n += 1;
    return n;
  }, [facettes, searchFilters, budgetNature]);

  // lot AV4 — biens modifiés non renvoyés du portefeuille chargé (compteur de l'onglet).
  // ⛔ Aucun calcul de message ici : on lit l'indicateur déjà servi par la route AV1.
  const biensModifies = useMemo(
    () => properties.filter(p => aUnSupportModifie(etatsDiffusion[p.id])),
    [properties, etatsDiffusion]);

  // Pagination
  const totalPages = Math.ceil(sortedAndFilteredProperties.length / itemsPerPage);
  // lot X2 — la page vient de l'adresse (lot V) : elle peut dépasser le nombre de pages réel
  // (recherche en page 3 puis bascule sur un onglet de 60 biens → écran vide). On la borne à la
  // dernière page valable, pas à la page 1, pour ne pas faire perdre le contexte.
  useEffect(() => {
    if (loading) return;
    // lot CE — le portefeuille arrive en deux temps (lot AG1) : la première réponse ne contient
    // que PREMIERE_PAGE biens, donc `totalPages` vaut 1 un court instant. Borner ici ramenait
    // à la page 1 le `page=2` que la flèche de retour venait tout juste de restituer.
    if (!chargementComplet) return;
    if (totalPages > 0 && currentPage > totalPages) setCurrentPage(totalPages);
    else if (totalPages === 0 && currentPage !== 1) setCurrentPage(1);
  }, [totalPages, currentPage, loading, chargementComplet]);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const sortedProperties = sortedAndFilteredProperties.slice(startIndex, startIndex + itemsPerPage);

  // === HELPERS ===
  
  // Formatage du type de bien (objet ou taxonomy.objet) en texte lisible
  const formatTypeBien = (property) => {
    const objet = property.objet || property.taxonomy?.objet || property.bien_general?.type_bien || property.profil;
    if (!objet) return 'NC';
    
    const labels = {
      'fonds_de_commerce': 'Fonds de commerce',
      'local_commercial': 'Local commercial',
      'bureaux': 'Bureaux',
      'murs_commerciaux': 'Murs commerciaux',
      'droit_au_bail': 'Droit au bail',
      'licence_exploitation': 'Licence de débit de boissons',
      'terrain': 'Terrain',
      'entrepot': 'Entrepôt',
      'local_activite': 'Local d\'activité / Entrepôt',
      'immeuble': 'Immeuble',
      'chr': 'CHR / hôtellerie-restauration',
      'retail': 'Commerce de détail (retail)',
      'services': 'Services non réglementés',
      'services_reglementes': 'Services réglementés',
      'production_reparation': 'Production / réparation / artisanat',
      'services_liberaux': 'Services & professions libérales',
      'industrie': 'Industrie'
    };
    
    return labels[objet] || objet.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
  };
  
  // Formatage du type d'opération (operation ou taxonomy.operation) en texte lisible
  const formatOperation = (property) => {
    const operation = property.operation || property.taxonomy?.operation;
    if (!operation) return 'NC';
    
    const labels = {
      'cession': 'Cession (PME/PMI & DAB)',
      'vente_immo': 'Vente immobilière (murs)',
      'location': 'Location pure',
      'location_droit_entree': 'Location + pas-de-porte',
      'location_gerance': 'Location-gérance'
    };
    
    return labels[operation] || operation.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
  };
  
  // lot V2 — ligne d'activité des cartes de la grille. Pour une vente de murs, l'activité n'a
  // pas de sens : on ne vend pas une activité, on vend un local. La ligne est structurellement
  // vide → on donne l'occupation (ce que cherche un investisseur), sinon RIEN. Pas de « N/C » :
  // le problème n'est pas le mot, c'est d'occuper une ligne pour dire qu'on ne sait pas.
  const activiteLigneCarte = (property, activite) => {
    if (activite && activite !== 'N/C') return activite;
    const operation = property.operation || property.taxonomy?.operation;
    const objet = property.objet || property.taxonomy?.objet;
    const venteDeMurs = operation === 'vente_immo' || objet === 'murs_commerciaux';
    if (venteDeMurs) {
      const occ = String(property.informations_vente?.occupation_murs || '').toLowerCase();
      if (occ === 'occupe') return 'Murs occupés';
      if (occ === 'libre') return 'Murs libres';
    }
    return '';
  };
  
  const formatPrice = (property) => {
    const operation = property.operation;
    const vente = property.informations_vente || {};
    const bail = property.informations_bail || {};
    const honoraires = property.honoraires_calcul || {};

    const fmtK = (v) => formatPriceFR(v);
    const fmtMois = (v) => formatRentFR(v);

    // Cas spécial : location avec pas-de-porte → DAB + loyer
    if (operation === 'location_droit_entree') {
      const droit = bail.droit_entree;
      const loyer = bail.loyer_mensuel;
      const droitStr = droit ? fmtK(droit) : 'N/C';
      const loyerStr = loyer ? fmtMois(loyer) : 'N/C';
      return `${droitStr} • ${loyerStr}`;
    }

    // Location-gérance → redevance
    if (operation === 'location_gerance') {
      return bail.redevance_mensuelle ? fmtMois(bail.redevance_mensuelle) : 'Prix NC';
    }

    // Location simple → loyer mensuel
    if (operation === 'location') {
      return bail.loyer_mensuel ? fmtMois(bail.loyer_mensuel) : 'Prix NC';
    }

    // Vente immobilière (murs) (vente_immo / vente_murs) → prix_annonce
    if (operation === 'vente_immo' || operation === 'vente_murs') {
      const prix = honoraires.prix_vente || vente.prix_annonce || vente.prix_cession;
      return prix ? fmtK(prix) : 'Prix NC';
    }

    // Cession (PME/PMI & DAB) (cession / vente_fonds / cession_bail / parts_sociales / licence en cession)
    // → prix_cession en priorité, puis prix_annonce
    const prix = honoraires.prix_vente || vente.prix_cession || vente.prix_annonce;
    if (prix) return fmtK(prix);

    // Fallback final : loyer si présent (rare mais possible si bien mal qualifié)
    if (bail.loyer_mensuel) return fmtMois(bail.loyer_mensuel);
    return 'Prix NC';
  };

  // === LOADING ===
  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center">
          <i className="bi bi-arrow-clockwise animate-spin text-4xl text-blue-600 mb-4 block"></i>
          <p className="text-slate-600">Chargement des biens...</p>
        </div>
      </div>
    );
  }

  // === RENDU LISTE ===
  // lot AV10/P3 bis — la carte est rendue par une fonction : la liste principale ET le
  // bloc « Légèrement au-dessus de votre budget » affichent la MÊME carte.
  const renderCarteBien = (property) => {
            // Utiliser les helpers pour mapping données
            const displayData = getPropertyListDisplayData(property);
            const statusBadge = getStatusBadge(property);
            const isSelected = selectedProperties.includes(property.id);
            const isFavorite = localFavorites.includes(property.id);
            const linkFromState = propertyLinks[property.id];
            // Fallback : utiliser share_token de la prop si présent (après reload, propertyLinks peut être vide)
            const link = linkFromState?.url ? linkFromState : (property.share_token ? { code: property.share_token, url: `${window.location.origin}/f/${property.share_token}`, loading: false } : null);
            
            return (
              <div key={property.id} className={`group bg-white rounded-xl overflow-hidden shadow-sm hover:shadow-xl transition-all border flex flex-col h-full ${isSelected ? 'border-blue-500 ring-2 ring-blue-200' : 'border-slate-200 hover:border-blue-200'}`}>
                {/* Photo avec overlays */}
                <div className="relative h-48 bg-slate-100 cursor-pointer" onClick={() => handleViewProperty(property)}>
                  {(() => {
                    // Vue cartes : on utilise THUMB directement (300px, ~17 KB) pour des cartes de ~350px.
                    // Pas de srcSet : éviter que le browser choisisse medium/large via la spec sizes.
                    // Si l'utilisateur veut une qualité supérieure, c'est sur la fiche détaillée.
                    const photoUrl = getPropertyMainPhotoUrl(property, 'thumb');
                    return photoUrl ? (
                      <img
                        src={photoUrl}
                        loading="lazy"
                        decoding="async"
                        alt=""
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                        onError={(e) => { e.target.style.display = 'none'; }}
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-blue-50 to-slate-100">
                        <i className="bi bi-building text-4xl text-slate-300"></i>
                      </div>
                    );
                  })()}
                  
                  {/* Overlay gradient */}
                  <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent pointer-events-none"></div>

                  {/* Bandeau diagonal VENDU / LOUÉ (standard immobilier) */}
                  {(statusBadge.label === 'Vendu' || statusBadge.label === 'Loué') && (
                    <div className="absolute inset-0 z-20 overflow-hidden pointer-events-none">
                      <div className="absolute left-1/2 top-1/2 w-[150%] -translate-x-1/2 -translate-y-1/2 -rotate-[18deg] bg-slate-900/85 text-white text-center text-base font-extrabold tracking-[0.3em] py-1.5 shadow-lg uppercase">
                        {statusBadge.label}
                      </div>
                    </div>
                  )}
                  
                  {/* Top Left: Checkbox + Favoris + Partagé (icône discrète) */}
                  <div className="absolute top-3 left-3 flex items-center gap-2 z-30" onClick={(e) => e.stopPropagation()}>
                    <input type="checkbox" checked={isSelected} onChange={(e) => { e.stopPropagation(); handleSelectProperty(property.id, e.target.checked); }}
                      className="w-5 h-5 rounded text-blue-600 border-2 border-white shadow-lg cursor-pointer" />
                    <button type="button" onClick={(e) => { e.stopPropagation(); toggleFavorite(property.id); }}
                      className="w-7 h-7 rounded-full bg-white/90 hover:bg-white shadow-lg flex items-center justify-center transition hover:scale-110 cursor-pointer">
                      <i className={`bi ${isFavorite ? 'bi-heart-fill text-red-500' : 'bi-heart text-slate-600'} pointer-events-none`}></i>
                    </button>
                    {(property.share_token || (property.is_public && property.public_code)) && (() => {
                      const publicUrl = link?.url || (property.share_token
                        ? `${window.location.origin}/f/${property.share_token}`
                        : `${window.location.origin}/p/${property.public_code}`);
                      return (
                        <a
                          href={publicUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={(e) => e.stopPropagation()}
                          title="Ouvrir la fiche publique"
                          aria-label="Ouvrir la fiche publique"
                          data-testid={`card-public-link-${property.id}`}
                          className="w-7 h-7 rounded-full bg-white/90 hover:bg-white shadow-lg flex items-center justify-center text-emerald-600 transition hover:scale-110 cursor-pointer"
                        >
                          <i className="bi bi-link-45deg text-sm pointer-events-none"></i>
                        </a>
                      );
                    })()}
                  </div>
                  
                  {/* Top Right: Badge transaction dans un cadre blanc "glass" */}
                  <div className="absolute top-3 right-3 z-10 pointer-events-none">
                    <div className="bg-white/90 backdrop-blur-sm rounded-lg shadow-md p-1 flex flex-col gap-1 items-stretch">
                      {(() => {
                        const badge = getTransactionBadge(property);
                        return (
                          <span className={`px-2 py-0.5 rounded-md text-[10px] font-bold ${badge.bgColor} ${badge.textColor} flex items-center justify-center gap-1`}>
                            <i className={`bi ${badge.icon}`}></i>
                            {badge.label}
                          </span>
                        );
                      })()}
                      {(() => {
                        // Badge "fonds en difficulté" — INTERNE STRICT (jamais sur vues publiques/partagées).
                        const proc = property.informations_confidentielles?.procedure;
                        if (!proc) return null;
                        const alerte = ['Dépôt de bilan', 'Liquidation judiciaire'].includes(proc);
                        const shortLabel = {
                          'Liquidation judiciaire': 'Liquidation',
                          'Dépôt de bilan': 'Dépôt de bilan',
                          'En procédure de résiliation': 'Résiliation bail',
                          'En renouvellement des 9 ans': 'Renouv. 9 ans',
                          'Sans renouvellement des 9 ans': 'Sans renouv.',
                        }[proc] || 'Procédure';
                        const cls = alerte ? 'bg-rose-100 text-rose-700' : 'bg-amber-100 text-amber-700';
                        return (
                          <span
                            className={`px-2 py-0.5 rounded-md text-[10px] font-bold ${cls} flex items-center justify-center gap-1`}
                            title={`Procédure (confidentiel) : ${proc}`}
                            data-testid={`card-procedure-badge-${property.id}`}
                          >
                            <i className={`bi ${alerte ? 'bi-exclamation-octagon-fill' : 'bi-exclamation-triangle-fill'}`}></i>
                            {shortLabel}
                          </span>
                        );
                      })()}
                    </div>
                  </div>
                  
                  {/* Bottom Left: Prix - STYLE PILL COMPACT */}
                  <div className="absolute bottom-4 left-4 z-10">
                    <div className="bg-white/95 backdrop-blur-sm px-3 py-1.5 rounded-lg shadow-lg">
                      <span className="text-sm font-bold text-slate-900 num">{formatPrice(property)}</span>
                    </div>
                  </div>
                  
                  {/* Bottom Right: Nombre de photos */}
                  {property.photosCount > 1 && (
                    <div className="absolute bottom-3 right-3 z-20 bg-black/70 text-white px-2 py-1 rounded-lg text-xs font-semibold flex items-center gap-1">
                      <i className="bi bi-images"></i>{property.photosCount}
                    </div>
                  )}
                </div>

                {/* Infos — lot AV3 : `gap-3` et NON `space-y-3`. 🔴 `space-y-*` génère
                    `> * + * { margin-top: .75rem }`, plus spécifique que `mt-auto` : le bloc
                    d'actions recevait 12 px de marge au lieu de `auto`, il suivait donc le
                    contenu et décrochait d'une ligne d'une carte à l'autre. Avec `gap`, les
                    enfants sont espacés sans marge et `mt-auto` reprend la main.
                    ⛔ Ni hauteur fixe, ni `justify-between` : le nombre de lignes
                    d'information varie d'une carte à l'autre, c'est tout l'intérêt. */}
                <div className="p-4 flex-1 flex flex-col gap-3">
                  {/* Référence + Statut */}
                  <div className="flex items-center justify-between">
                    <span className="text-[11px] font-mono bg-slate-100 px-2.5 py-1 rounded text-slate-600">
                      #{displayData.reference}
                    </span>
                    <span className={`text-[11px] px-2.5 py-1 rounded-lg font-semibold ${statusBadge.class}`}>
                      {statusBadge.label}
                    </span>
                  </div>
                  
                  {/* Type (Titre principal) */}
                  <div>
                    <h3 className="font-semibold text-slate-900 text-base">{displayData.typeBien}</h3>
                    {/* lot V2 — hauteur conservée (grille alignée) même sans texte */}
                    <p className="text-sm text-slate-500 mt-0.5 min-h-[1.25rem]" data-testid={`property-card-activite-${property.id}`}>
                      {activiteLigneCarte(property, displayData.activite) || '\u00A0'}
                    </p>
                  </div>
                  
                  {/* 🔴 lot AV3 — les quatre lignes d'information sont le cœur de la carte :
                      ville · surface · occupation · date, chacune avec son icône. Une ligne
                      dont l'information manque n'est PAS affichée — pas de tiret, pas de
                      « NC ». ⛔ Ne jamais les compresser en une seule ligne : l'occupation et
                      la date y disparaîtraient, et c'est ce qui rend la carte utile. */}
                  <div className="space-y-1.5 text-sm">
                    {/* Ville — ⛔ les valeurs sentinelles du mapper (« Ville NC », « N/C ») ne
                        sont PAS des informations : la ligne disparaît au lieu de les afficher. */}
                    {displayData.ville && !['NC', 'N/C', 'Ville NC'].includes(displayData.ville) && (
                      <div className="flex items-center gap-2 text-slate-600">
                        <i className="bi bi-geo-alt text-[#007BFF]"></i>
                        <span className="font-medium">{displayData.ville}</span>
                      </div>
                    )}

                    {/* Surface */}
                    {(() => {
                      const surfaceTexte = (displayData.surfaceLabel && displayData.surfaceLabel !== 'N/C')
                        ? displayData.surfaceLabel
                        : (displayData.surface ? formatSurfaceFR(displayData.surface) : null);
                      return surfaceTexte ? (
                        <div className="flex items-center gap-2 text-slate-600">
                          <i className="bi bi-rulers text-[#007BFF]"></i>
                          <span className="num">{surfaceTexte}</span>
                        </div>
                      ) : null;
                    })()}
                    
                    {/* Infos Services Libéraux (si profil = services_liberaux) */}
                    {displayData.servicesLiberauxInfo && (
                      <div className="flex items-center gap-2 text-slate-600">
                        <i className="bi bi-layout-text-sidebar-reverse text-slate-500"></i>
                        <span className="text-xs">{displayData.servicesLiberauxInfo}</span>
                      </div>
                    )}
                    
                    {/* Statut du bien (Libre/Loué) */}
                    {displayData.statut && (
                      <div className="flex items-center gap-2 text-slate-600">
                        <i className={`bi ${displayData.statut === 'Libre' ? 'bi-check-circle text-green-500' : 'bi-dash-circle text-orange-500'}`}></i>
                        <span>{displayData.statut}</span>
                      </div>
                    )}

                    {/* Rendement brut (murs occupés) — signal investisseur */}
                    {property.informations_vente?.occupation_murs === 'occupe' && property.informations_vente?.taux_rendement_brut && (
                      <div className="flex items-center gap-2" data-testid="card-rendement-badge">
                        <i className="bi bi-graph-up-arrow text-amber-500"></i>
                        <span className="text-amber-700 font-semibold">Rendement brut {property.informations_vente.taux_rendement_brut}%</span>
                        <span className="text-[10px] bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded font-medium">Occupé</span>
                      </div>
                    )}
                    
                    {/* Date */}
                    {property.created_at && (
                      <div className="flex items-center gap-2 text-slate-500">
                        <i className="bi bi-calendar3"></i>
                        <span className="text-xs num">{new Date(property.created_at).toLocaleDateString('fr-FR')}</span>
                      </div>
                    )}
                  </div>

                  {/* lot AV3 — ligne de diffusion : les étiquettes à gauche, l'action à droite.
                      Le bouton devient « Renvoyer » dès qu'un support est modifié non renvoyé. */}
                  <div className="flex items-center justify-between gap-2 pt-2 border-t border-slate-100"
                       data-testid={`card-diffusion-row-${property.id}`}>
                    <DiffusionBadges etats={etatsDiffusion[property.id]} propertyId={property.id} />
                    {aUnSupportModifie(etatsDiffusion[property.id]) ? (
                      <button
                        type="button"
                        onClick={(e) => { e.stopPropagation(); ouvrirGestionDiffusion(property); }}
                        data-testid={`card-renvoyer-${property.id}`}
                        className="shrink-0 inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg border border-amber-300 bg-white text-xs font-semibold text-amber-800 hover:bg-amber-50 transition"
                      >
                        <i className="bi bi-arrow-repeat" />Renvoyer
                      </button>
                    ) : (
                      <button
                        type="button"
                        onClick={(e) => { e.stopPropagation(); ouvrirGestionDiffusion(property); }}
                        data-testid={`card-diffuser-${property.id}`}
                        className="shrink-0 inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg border border-slate-200 bg-white text-xs font-semibold text-slate-700 hover:bg-blue-50 transition"
                      >
                        <i className="bi bi-broadcast text-[#28A745]" />Diffuser
                      </button>
                    )}
                  </div>

                  {/* lot AV6 — un seul langage de boutons : motif de la barre CRM de la fiche
                      (bordure fine, fond blanc, icône colorée, libellé gris, survol blue-50).
                      🔴 « Modifier » garde le bleu plein : décision prise, c'est l'action
                      principale de la carte. `mt-auto` colle le bloc en bas — c'est lui qui
                      aligne les cartes d'une même rangée. */}
                  <div className="mt-auto pt-3 border-t border-slate-100 flex items-center gap-2">
                    <button onClick={(e) => { e.stopPropagation(); handleViewProperty(property); }}
                      data-testid={`card-voir-${property.id}`}
                      className="flex-1 min-h-[44px] bg-white border border-slate-200 hover:bg-blue-50 text-slate-700 py-2.5 rounded-lg text-sm font-medium flex items-center justify-center gap-2 transition">
                      <i className="bi bi-eye text-[#007BFF]"></i>Voir
                    </button>
                    <button onClick={(e) => { e.stopPropagation(); handleEditProperty(property); }}
                      data-testid={`card-modifier-${property.id}`}
                      className="flex-1 min-h-[44px] bg-[#0062CC] hover:bg-[#0056b3] text-white py-2.5 rounded-lg text-sm font-semibold flex items-center justify-center gap-2 transition shadow-sm">
                      <i className="bi bi-pencil"></i>Modifier
                    </button>
                    
                    {/* Menu ... */}
                    <div className="relative">
                      <button 
                        onClick={(e) => { e.stopPropagation(); setActiveDropdown(activeDropdown === property.id ? null : property.id); }}
                        className="min-h-[44px] bg-white border border-slate-200 hover:bg-blue-50 text-slate-600 p-2.5 rounded-lg transition"
                        title="Plus d'actions"
                        aria-label="Plus d'actions"
                        data-testid={`property-kebab-${property.id}`}
                      >
                        <i className="bi bi-three-dots-vertical"></i>
                      </button>
                      {activeDropdown === property.id && renderKebabDropdown(property)}
                    </div>
                  </div>
                  
                  {/* Lien de partage expandé (compact, juste Copier + Ouvrir + QR) */}
                  {expandedShareProperty === property.id && link && (
                    <div className="mt-3 p-3 bg-blue-50 rounded-lg border border-blue-200">
                      <p className="text-[11px] text-blue-700 mb-2 font-medium">
                        <i className="bi bi-link-45deg mr-1"></i>Lien de partage public actif
                      </p>
                      <div className="flex gap-3 items-start">
                        {/* QR Code (si dispo) */}
                        {link.qr_code && (
                          <div className="flex-shrink-0 bg-white p-1.5 rounded border border-blue-200">
                            <img
                              src={link.qr_code}
                              alt="QR Code de la fiche publique"
                              className="w-20 h-20 block"
                              data-testid={`share-qr-${property.id}`}
                            />
                            <p className="text-[9px] text-center text-slate-500 mt-0.5 font-medium">Scanner</p>
                          </div>
                        )}
                        {/* Boutons à droite */}
                        <div className="flex-1 flex flex-col gap-2">
                          <button
                            onClick={(e) => { e.stopPropagation(); copyToClipboard(link.url); }}
                            className="px-3 py-2 bg-blue-600 hover:bg-blue-700 text-white text-xs rounded font-semibold flex items-center justify-center gap-1.5"
                            data-testid={`share-copy-${property.id}`}
                          >
                            <i className="bi bi-clipboard"></i>Copier le lien
                          </button>
                          <a
                            href={link.url} target="_blank" rel="noopener noreferrer"
                            onClick={(e) => e.stopPropagation()}
                            className="px-3 py-2 bg-green-600 hover:bg-green-700 text-white text-xs rounded font-semibold flex items-center justify-center gap-1.5"
                            data-testid={`share-open-${property.id}`}
                          >
                            <i className="bi bi-box-arrow-up-right"></i>Voir la fiche
                          </a>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
    );
  };

  // lot CW — liste des onglets de statut définie UNE SEULE FOIS : elle alimente le strip
  // (ordinateur) et le `<select>` (téléphone), qui ne peuvent donc plus divergerir.
  const onglets = [
    { key: 'all', label: 'Tous', count: counts.all },
    { key: 'draft', label: 'Brouillons', count: counts.draft },
    // 🔴 lot AV4 — « Actifs » et non « En ligne » : « en ligne » est désormais réservé
    // à la diffusion (une annonce en ligne sur un portail). ⛔ Seul le LIBELLÉ change,
    // la clé, le filtre et les tests restent sur `active`.
    { key: 'active', label: 'Actifs', count: counts.active },
    // lot AV4 — libellé COURT : « Modifiés non renvoyés » faisait 227 px et faisait
    // déborder le strip de 13 px. Un onglet est un filtre ; l'étiquette sur la carte,
    // elle, reste « Modifié — non renvoyé ».
    { key: 'modifies', label: 'Non renvoyés', count: biensModifies.length, color: 'amber', icon: 'bi-pencil-square' },
    { key: 'archive', label: 'Archivés', count: counts.archive, color: 'amber' },
    { key: 'sold', label: 'Vendus', count: counts.sold },
    { key: 'shared', label: 'Partagés', count: counts.shared, color: 'emerald', icon: 'bi-link-45deg' },
  ];
  // Sur ordinateur, « Supprimés » reste un bouton à part (lot BZ) ; sur téléphone il n'y a plus
  // de strip, l'option rejoint donc le menu — et seulement pour l'administrateur.
  const ongletsMobile = estAdmin
    ? [...onglets, { key: 'deleted', label: 'Supprimés', count: counts.deleted }]
    : onglets;

  // lot DK2 — libelle lisible en 14 px, selecteur natif en 16 px (cf. SelecteurTelephone)
  const statutMobile = (
    <SelecteurTelephone
      value={currentFilter}
      onChange={(e) => handleFilterChange(e.target.value)}
      options={ongletsMobile.map(t => ({ valeur: t.key, libelle: `${t.label} (${t.count})` }))}
      ariaLabel="Filtrer par statut"
      testId="tab-select-mobile"
      className="flex-1"
    />
  );

  // Les quatre tris utiles en un seul réglage. Un `<select>` natif n'affiche que l'option
  // choisie : les libellés sont donc courts, le sens est porté par la flèche et l'infobulle.
  const TRIS = [
    { valeur: 'created_at:desc', libelle: 'Création ↓', titre: 'Création — récents d’abord' },
    { valeur: 'created_at:asc', libelle: 'Création ↑', titre: 'Création — anciens d’abord' },
    { valeur: 'updated_at:desc', libelle: 'Modification ↓', titre: 'Modification — récents d’abord' },
    { valeur: 'updated_at:asc', libelle: 'Modification ↑', titre: 'Modification — anciens d’abord' },
  ];

  const triMobile = (
    <SelecteurTelephone
      value={`${sortColumn}:${sortOrder}`}
      onChange={(e) => {
        const [colonne, ordre] = e.target.value.split(':');
        setSortColumn(colonne);
        setSortOrder(ordre);
      }}
      options={TRIS.map(t => ({ valeur: t.valeur, libelle: t.libelle }))}
      ariaLabel="Trier les biens"
      testId="sort-select-mobile"
      className="flex-1"
    />
  );


  return (
    <div className="p-3 md:p-6 space-y-4 bg-slate-50 min-h-screen champs-16-mobile">
      {/* === HEADER === */}
      <div className="flex flex-row items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          {/* lot CW — sur téléphone, une seule ligne : le carré d'icône laisse la place au bouton. */}
          <div className="hidden md:flex w-10 h-10 sm:w-12 sm:h-12 rounded-xl bg-blue-100 items-center justify-center">
            <i className="bi bi-building text-blue-600 text-xl sm:text-2xl"></i>
          </div>
          <div>
            <h1 className="text-xl sm:text-2xl font-bold text-slate-800">Mes Biens</h1>
            <p className="text-xs sm:text-sm text-slate-500 hidden sm:block">Gérez votre portefeuille immobilier</p>
          </div>
        </div>
        {/* lot DK2 — telephone : la creation passe par le bouton « + » de la barre du bas */}
        <button
          onClick={handleNewProperty}
          className="shrink-0 hidden md:flex px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-semibold shadow-sm transition items-center gap-2"
        >
          <i className="bi bi-plus-circle-fill"></i>
          <span className="md:hidden">Nouveau</span>
          <span className="hidden md:inline">Nouveau Bien</span>
        </button>
      </div>

      {/* === RECHERCHE À FACETTES (lot AV10) ===
          Les six champs fixes (référence, négociateur, transaction, catégorie, type de bien,
          photos) sont remplacés par UNE barre + un panneau de facettes cumulatives. */}
      <FacetSearchBar
        reference={searchFilters.reference}
        onReference={(v) => { setSearchFilters(p => ({ ...p, reference: v })); setCurrentPage(1); }}
        facettes={facettes}
        onToggle={basculerFacette}
        bornes={{
          budgetMin: searchFilters.budgetMin, budgetMax: searchFilters.budgetMax,
          surfaceMin: searchFilters.surfaceMin, surfaceMax: searchFilters.surfaceMax,
        }}
        budgetNature={budgetNature}
        budgetMelange={(searchFilters.operations || []).length > 0 && !budgetNature}
        regroupements={libellesRegroupes}
        onBorne={(cle, v) => { setSearchFilters(p => ({ ...p, [cle]: v })); setCurrentPage(1); }}
        pastilles={pastilles}
        nbFiltresActifs={nbFiltresPanneau}
        resultats={sortedAndFilteredProperties.length}
        comptagePret={chargementComplet}
        ouvert={panneauFacettes}
        setOuvert={setPanneauFacettes}
        onReset={reinitialiserFiltres}
        statutMobile={statutMobile}
        triMobile={triMobile}
      />

      {/* === ONGLETS DE STATUT (masqués sur téléphone : lot CW, remplacés par `tab-select-mobile`) === */}
      <div className="hidden md:flex bg-white border border-slate-200 rounded-xl p-4 shadow-sm flex-col lg:flex-row items-start lg:items-center justify-between gap-4">
        {/* lot AV9 — `tabs-scroll-x` (index.css) : scrollbar masquée, scroll-snap doux et
            fondu sur les bords pour dire qu'il y a plus. ⛔ Pas d'`overflow-x-auto` brut, qui
            laissait une barre de défilement bien visible dès que le strip débordait. */}
        <div className="tabs-scroll-x tabs-wrap-lg lg:flex-1 lg:min-w-0 flex items-center gap-2 flex-nowrap w-full lg:w-auto -mx-1 px-1"
             data-testid="properties-tabs">
          {onglets.filter(Boolean).map((tab) => (
            <button key={tab.key} data-testid={`tab-${tab.key}`} onClick={() => handleFilterChange(tab.key)}
              className={`px-4 py-2 min-h-[44px] shrink-0 rounded-lg text-sm font-semibold transition flex items-center gap-2 ${
                currentFilter === tab.key
                  ? tab.color === 'amber' ? 'bg-amber-500 text-white'
                    : tab.color === 'emerald' ? 'bg-emerald-500 text-white'
                    : tab.color === 'red' ? 'bg-red-600 text-white'
                    : 'bg-blue-600 text-white'
                  : 'text-slate-600 hover:bg-slate-50'
              }`}>
              {tab.icon && <i className={`bi ${tab.icon} text-xs`}></i>}
              <span>{tab.label}</span>
              <span className={`px-2 py-0.5 rounded-full text-xs font-bold ${
                currentFilter === tab.key
                  ? tab.color === 'amber' ? 'bg-amber-600'
                    : tab.color === 'emerald' ? 'bg-emerald-600'
                    : tab.color === 'red' ? 'bg-red-700' : 'bg-blue-600'
                  : 'bg-slate-100 text-slate-700'
              }`}>{tab.count}</span>
            </button>
          ))}
        </div>
        {/* lot BZ — « Supprimés » sort de la barre : dans le strip, il finissait coupé sous le
            sélecteur Grille/Liste. Bouton discret, visible sur TOUTES les tailles d'écran. */}
        <div className="flex items-center gap-2 shrink-0">
          {estAdmin && (
            <button
              onClick={() => handleFilterChange('deleted')}
              data-testid="tab-deleted"
              title="Biens supprimés — restauration réservée à l'administrateur"
              className={`min-h-[44px] px-3 py-2 rounded-lg border text-sm font-medium flex items-center gap-2 whitespace-nowrap transition-colors ${
                currentFilter === 'deleted'
                  ? 'bg-red-50 text-red-600 border-red-600'
                  : 'bg-transparent text-slate-500 border-slate-200 hover:text-red-600'
              }`}>
              <i className="bi bi-trash"></i>
              <span>Supprimés</span>
              <span className={`px-2 py-0.5 rounded-full text-xs font-bold ${
                currentFilter === 'deleted' ? 'bg-red-100 text-red-700' : 'bg-slate-100 text-slate-700'
              }`}>{counts.deleted}</span>
            </button>
          )}
          <div className="hidden md:flex items-center bg-slate-100 rounded-lg p-1" data-testid="properties-view-toggle">
          <button data-testid="view-grid" onClick={() => setViewMode('grid')} className={`px-3 py-1.5 flex items-center gap-2 rounded-md text-sm font-medium ${viewMode === 'grid' ? 'bg-white text-blue-600 shadow-sm' : 'text-slate-600'}`}>
            <i className="bi bi-grid-3x3-gap"></i><span className="hidden sm:inline">Grille</span>
          </button>
          <button data-testid="view-list" onClick={() => setViewMode('list')} className={`px-3 py-1.5 flex items-center gap-2 rounded-md text-sm font-medium ${viewMode === 'list' ? 'bg-white text-blue-600 shadow-sm' : 'text-slate-600'}`}>
            <i className="bi bi-list-ul"></i><span className="hidden sm:inline">Liste</span>
          </button>
          </div>
        </div>
      </div>

      {/* === BARRE D'ACTIONS === */}
      <div className="bg-white border border-slate-200 rounded-xl p-3 md:p-4 shadow-sm flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
        <div className="w-full md:w-auto flex items-center justify-between md:justify-start gap-4 shrink-0">
          <label className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer">
            <input type="checkbox" className="w-4 h-4 rounded text-blue-600 focus:ring-blue-500 border-slate-300"
              checked={selectedProperties.length > 0 && selectedProperties.length === sortedProperties.length}
              onChange={(e) => handleSelectAll(e.target.checked)} />
            <span className="font-medium whitespace-nowrap">Tout sélectionner</span>
          </label>
          <span className="text-[13px] md:text-sm text-slate-500 whitespace-nowrap" data-testid="properties-selection-count">
            {/* lot AV7 — « X / Y » désignait la PAGE courante, pas le portefeuille, et laissait
                croire à une limite. ⛔ Le vrai plafond (dossier groupé) est dit ailleurs.
                P2 bis — accord réel : « 1 bien sélectionné » / « 12 biens sélectionnés ». */}
            {selectedProperties.length > 0
              ? `${selectedProperties.length} bien${selectedProperties.length > 1 ? 's' : ''} sélectionné${selectedProperties.length > 1 ? 's' : ''}`
              : `${sortedProperties.length} bien${sortedProperties.length > 1 ? 's' : ''}`}
          </span>
        </div>

        {/* Actions groupées — lot AV7 : « Diffuser » en tête, en vert plein (seule action qui
            publie à l'extérieur) ; « Archiver » en ambre (ranger n'est pas détruire),
            « Supprimer » en rouge ; les actions neutres prennent l'icône bleue.
            lot AV9 — à 375 px, deux actions visibles au maximum (Diffuser et PDF) : le reste
            passe dans le ⋮ en feuille montante, libellés en toutes lettres. */}
        {selectedProperties.length > 0 && (
          <div className="flex flex-nowrap items-center gap-2" data-testid="bulk-actions-bar">
            <button
              onClick={() => ouvrirLotDiffusion('activate', properties.filter(p => selectedProperties.includes(p.id)))}
              className="px-3 py-2 min-h-[44px] bg-[#28A745] hover:bg-[#218838] text-white rounded-lg text-sm font-semibold flex items-center gap-1.5 shadow-sm"
              data-testid="bulk-diffuser-btn"
            >
              <i className="bi bi-broadcast"></i><span>Diffuser</span>
            </button>
            {selectedProperties.some(id => aUnSupportModifie(etatsDiffusion[id])) && (
              <button
                onClick={() => ouvrirLotDiffusion('update', properties.filter(p => selectedProperties.includes(p.id)))}
                className="px-3 py-2 min-h-[44px] bg-white border border-amber-300 text-amber-800 hover:bg-amber-50 rounded-lg text-sm font-semibold flex items-center gap-1.5 shadow-sm"
                data-testid="bulk-renvoyer-btn"
              >
                <i className="bi bi-arrow-repeat"></i>
                <span>
                  Renvoyer ({selectedProperties.filter(id => aUnSupportModifie(etatsDiffusion[id])).length})
                </span>
              </button>
            )}
            {/* Menu PDF avec dropdown pour actions groupées */}
            <div className="relative group/bulkpdf">
              <button 
                className="px-3 py-2 min-h-[44px] bg-white border border-slate-200 text-slate-700 hover:bg-slate-50 hover:text-slate-900 rounded-lg text-sm font-medium flex items-center gap-1.5 shadow-sm disabled:opacity-50 disabled:cursor-not-allowed"
                disabled={bulkPdfProgress.active || selectedProperties.length > MAX_BIENS_DOSSIER}
                title={selectedProperties.length > MAX_BIENS_DOSSIER ? MESSAGE_PLAFOND_DOSSIER : undefined}
                data-testid="bulk-pdf-btn"
              >
                {bulkPdfProgress.active ? (
                  <>
                    <i className="bi bi-hourglass-split animate-spin"></i>
                    <span className="hidden sm:inline">{bulkPdfProgress.current}/{bulkPdfProgress.total}</span>
                  </>
                ) : (
                  <>
                    <i className="bi bi-file-pdf"></i>
                    <span className="hidden sm:inline">PDF ({selectedProperties.length})</span>
                  </>
                )}
                <i className="bi bi-chevron-down text-xs ml-1 text-slate-400"></i>
              </button>
              {/* lot G — au-delà du plafond, la génération n'est pas proposée : l'utilisateur
                  ne doit pas déclencher un dossier voué à l'échec puis attendre l'erreur. */}
              {selectedProperties.length > MAX_BIENS_DOSSIER && (
                <span className="absolute left-0 top-full mt-1 z-50 w-64 bg-amber-50 border border-amber-200 text-amber-800 text-xs rounded-lg px-3 py-2 shadow-sm"
                      data-testid="bulk-pdf-limit-msg">
                  {MESSAGE_PLAFOND_DOSSIER}
                </span>
              )}
              {!bulkPdfProgress.active && selectedProperties.length <= MAX_BIENS_DOSSIER && (
                <div className="absolute left-0 top-full mt-1 bg-white border border-slate-200 rounded-lg shadow-xl z-50 opacity-0 invisible group-hover/bulkpdf:opacity-100 group-hover/bulkpdf:visible transition-all min-w-[200px]">
                  <div className="py-1">
                    <button 
                      onClick={() => handleBulkGeneratePDF('confidential')}
                      className="w-full text-left px-4 py-2 text-sm text-slate-700 hover:bg-slate-50 flex items-center gap-2"
                    >
                      <i className="bi bi-file-earmark-lock text-slate-500"></i>
                      <div>
                        <span className="font-medium">Fiches Agent</span>
                        <p className="text-xs text-slate-400">Avec confidentiel</p>
                      </div>
                    </button>
                    <button 
                      onClick={() => handleBulkGeneratePDF('public')}
                      className="w-full text-left px-4 py-2 text-sm text-slate-700 hover:bg-slate-50 flex items-center gap-2"
                    >
                      <i className="bi bi-file-earmark-text text-slate-500"></i>
                      <div>
                        <span className="font-medium">Fiches Client</span>
                        <p className="text-xs text-slate-400">Sans confidentiel</p>
                      </div>
                    </button>
                  </div>
                </div>
              )}
            </div>
            {/* lot AV1-bis/P2bis — 🔴 quatre actions exposées, aucune destructive. « Archiver »
                (ambre) et « Supprimer » (rouge) passent sous le ⋮, en dernier et séparés : une
                action destructive en lot à un clic d'une action anodine, sur une sélection qui
                peut porter des dizaines de biens, c'est le clic de trop que le manifeste
                interdit (DESIGN_SYSTEM, « Actions de ligne »). */}
            <div className="hidden sm:flex items-center gap-2">
            <button onClick={handleBulkShare} className="px-3 py-2 min-h-[44px] bg-white border border-slate-200 text-slate-700 hover:bg-blue-50 rounded-lg text-sm font-medium flex items-center gap-1.5 shadow-sm" data-testid="bulk-share-btn">
              <i className="bi bi-share text-[#007BFF]"></i><span className="hidden lg:inline">Partager</span>
            </button>
            <button onClick={handleBulkEmail} className="px-3 py-2 min-h-[44px] bg-white border border-slate-200 text-slate-700 hover:bg-blue-50 rounded-lg text-sm font-medium flex items-center gap-1.5 shadow-sm" data-testid="bulk-email-btn">
              <i className="bi bi-envelope text-[#007BFF]"></i><span className="hidden lg:inline">Email</span>
            </button>
            </div>

            {/* Le ⋮ de la barre : menu déroulant sur grand écran, feuille montante à 375 px. */}
            <div className="relative">
              <button
                type="button"
                onClick={() => {
                  if (window.innerWidth < 640) setFeuilleActionsOuverte(true);
                  else setMenuLotOuvert(!menuLotOuvert);
                }}
                className="px-3 py-2 min-h-[44px] bg-white border border-slate-200 text-slate-700 hover:bg-blue-50 rounded-lg text-sm font-medium shadow-sm flex items-center gap-1.5"
                data-testid="bulk-more-btn"
                aria-label="Plus d'actions groupées"
              >
                <i className="bi bi-three-dots-vertical"></i>
              </button>
              {menuLotOuvert && (
                <div className="hidden sm:block absolute right-0 top-full mt-1 bg-white border border-slate-200 rounded-lg shadow-xl z-[60] min-w-[220px] py-1"
                     data-testid="bulk-more-menu">
                  <button onClick={() => { setMenuLotOuvert(false); handleBulkToggleFavorites(); }}
                    className="w-full text-left px-4 py-2 min-h-[44px] text-sm text-slate-700 hover:bg-blue-50 flex items-center gap-2"
                    data-testid="bulk-favorites-btn">
                    <i className={`bi ${selectedProperties.every(id => localFavorites.includes(id)) ? 'bi-heart-fill' : 'bi-heart'} text-[#007BFF]`}></i>
                    {selectedProperties.every(id => localFavorites.includes(id)) ? 'Retirer des favoris' : 'Ajouter aux favoris'}
                  </button>
                  <button onClick={() => { setMenuLotOuvert(false); handleBulkAffectClient(); }}
                    className="w-full text-left px-4 py-2 min-h-[44px] text-sm text-slate-700 hover:bg-blue-50 flex items-center gap-2"
                    data-testid="bulk-affect-btn">
                    <i className="bi bi-person-check text-[#007BFF]"></i>Affecter à un client
                  </button>
                  {/* Ambre et rouge en dernier, séparés — même motif que le ⋮ de la carte. */}
                  <div className="border-t border-slate-100 my-1"></div>
                  {currentFilter === 'archive' || currentFilter === 'deleted' ? (
                    <button onClick={() => { setMenuLotOuvert(false); handleBulkRestore(); }}
                      className="w-full text-left px-4 py-2 min-h-[44px] text-sm text-slate-700 hover:bg-blue-50 flex items-center gap-2"
                      data-testid="bulk-restore-btn">
                      <i className="bi bi-arrow-counterclockwise text-[#007BFF]"></i>Restaurer
                    </button>
                  ) : (
                    <button onClick={() => { setMenuLotOuvert(false); handleBulkArchive(); }}
                      className="w-full text-left px-4 py-2 min-h-[44px] text-sm text-amber-800 hover:bg-amber-50 flex items-center gap-2"
                      data-testid="bulk-archive-btn">
                      <i className="bi bi-archive text-amber-600"></i>Archiver
                    </button>
                  )}
                  {/* lot BY — groupé : admin, et seulement depuis l'onglet « Archivés ». */}
                  {estAdmin && currentFilter === 'archive' && (
                    <button onClick={() => { setMenuLotOuvert(false); handleBulkDelete(); }}
                      className="w-full text-left px-4 py-2 min-h-[44px] text-sm text-red-600 hover:bg-red-50 flex items-center gap-2"
                      data-testid="bulk-delete-btn">
                      <i className="bi bi-trash"></i>Supprimer
                    </button>
                  )}
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* lot AV9 — feuille montante des actions groupées (mobile). */}
      {feuilleActionsOuverte && (
        <div className="sm:hidden fixed inset-0 z-[65] bg-black/40 flex items-end"
             onClick={() => setFeuilleActionsOuverte(false)} data-testid="bulk-actions-sheet">
          <div className="bg-white w-full rounded-t-2xl p-4 space-y-1" onClick={(e) => e.stopPropagation()}>
            <p className="text-xs font-semibold tracking-wider uppercase text-slate-500 px-2 pb-2">
              {selectedProperties.length} bien{selectedProperties.length > 1 ? 's' : ''} sélectionné{selectedProperties.length > 1 ? 's' : ''}
            </p>
            {[
              { label: 'Partager', icone: 'bi-share', action: handleBulkShare, testid: 'sheet-share' },
              { label: 'Email', icone: 'bi-envelope', action: handleBulkEmail, testid: 'sheet-email' },
              { label: 'Favoris', icone: 'bi-heart', action: handleBulkToggleFavorites, testid: 'sheet-favorites' },
              { label: 'Affecter client', icone: 'bi-person-check', action: handleBulkAffectClient, testid: 'sheet-affect' },
            ].map(a => (
              <button key={a.testid} type="button" data-testid={a.testid}
                onClick={() => { setFeuilleActionsOuverte(false); a.action(); }}
                className="w-full min-h-[44px] flex items-center gap-3 px-3 rounded-lg text-sm text-slate-700 hover:bg-blue-50">
                <i className={`bi ${a.icone} text-[#007BFF]`}></i>{a.label}
              </button>
            ))}
            <div className="border-t border-slate-100 my-1"></div>
            {currentFilter === 'archive' || currentFilter === 'deleted' ? (
              <button type="button" data-testid="sheet-restore"
                onClick={() => { setFeuilleActionsOuverte(false); handleBulkRestore(); }}
                className="w-full min-h-[44px] flex items-center gap-3 px-3 rounded-lg text-sm text-slate-700 hover:bg-blue-50">
                <i className="bi bi-arrow-counterclockwise text-[#007BFF]"></i>Restaurer
              </button>
            ) : (
              <button type="button" data-testid="sheet-archive"
                onClick={() => { setFeuilleActionsOuverte(false); handleBulkArchive(); }}
                className="w-full min-h-[44px] flex items-center gap-3 px-3 rounded-lg text-sm text-amber-800 hover:bg-amber-50">
                <i className="bi bi-archive text-amber-600"></i>Archiver
              </button>
            )}
            {estAdmin && currentFilter === 'archive' && (
              <button type="button" data-testid="sheet-delete"
                onClick={() => { setFeuilleActionsOuverte(false); handleBulkDelete(); }}
                className="w-full min-h-[44px] flex items-center gap-3 px-3 rounded-lg text-sm text-red-600 hover:bg-red-50">
                <i className="bi bi-trash"></i>Supprimer
              </button>
            )}
          </div>
        </div>
      )}

      {/* === LISTE DES BIENS (GRILLE) === */}
      {error ? (
        <div className="bg-red-50 border border-red-200 rounded-xl p-4 text-red-700">
          <i className="bi bi-exclamation-triangle mr-2"></i>{error}
        </div>
      ) : sortedProperties.length === 0 ? (
        <div className="bg-white rounded-xl border border-slate-200 p-12 text-center">
          <i className="bi bi-inbox text-6xl text-slate-300 mb-4 block"></i>
          <h3 className="text-xl font-semibold text-slate-800 mb-2">Aucun bien trouvé</h3>
          <p className="text-slate-600 mb-6">{currentFilter === 'all' ? 'Créez votre premier bien' : `Aucun bien "${currentFilter}"`}</p>
          <button onClick={handleNewProperty} className="px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded-lg shadow-sm">
            <i className="bi bi-plus-circle mr-2"></i>Créer un bien
          </button>
        </div>
      ) : viewMode === 'grid' ? (
        <>
          {/* Pagination du haut (grille) — masquée sur téléphone (lot CW) : celle du bas suffit.
              lot DK3 — Précédent / Suivant restaient hors du cadre entre 768 et 1 279 px. */}
          {sortedAndFilteredProperties.length > itemsPerPage && (
            <div className="hidden md:flex flex-wrap items-center justify-between gap-3 bg-white border border-slate-200 rounded-lg p-3 mb-4">
              <div className="flex items-center gap-3">
                <span className="text-sm text-slate-600">
                  Affichage de {startIndex + 1}-{Math.min(startIndex + itemsPerPage, sortedAndFilteredProperties.length)} sur {sortedAndFilteredProperties.length} biens
                </span>
                <select 
                  value={itemsPerPage} 
                  onChange={(e) => { setItemsPerPage(Number(e.target.value)); setCurrentPage(1); }}
                  className="border border-slate-300 rounded px-2 py-1 text-sm"
                >
                  <option value={10}>10 par page</option>
                  <option value={20}>20 par page</option>
                  <option value={50}>50 par page</option>
                  <option value={100}>100 par page</option>
                </select>
                
                {/* Sélecteur de tri */}
                <div className="flex items-center gap-2 ml-4 border-l border-slate-300 pl-4">
                  <label className="text-sm text-slate-600 font-medium">Trier par :</label>
                  <select 
                    value={sortColumn}
                    onChange={(e) => setSortColumn(e.target.value)}
                    className="px-3 py-1 border border-slate-300 rounded text-sm bg-white hover:border-blue-400 focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                  >
                    <option value="created_at">Date de création</option>
                    <option value="updated_at">Dernière modification</option>
                  </select>
                  
                  <button
                    onClick={() => setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')}
                    className="px-3 py-1 border border-slate-300 rounded text-sm bg-white hover:bg-slate-50 flex items-center gap-1"
                    title={sortOrder === 'asc' ? 'Ordre croissant' : 'Ordre décroissant'}
                  >
                    <i className={`bi ${sortOrder === 'asc' ? 'bi-sort-up' : 'bi-sort-down'} text-blue-600`}></i>
                    {sortOrder === 'asc' ? 'Croissant' : 'Décroissant'}
                  </button>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <button 
                  onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                  disabled={currentPage === 1}
                  className="px-3 py-1 border border-slate-300 rounded text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-slate-50"
                >
                  Précédent
                </button>
                <span className="text-sm text-slate-600">
                  Page {currentPage} sur {totalPages}
                </span>
                <button 
                  onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                  disabled={currentPage === totalPages}
                  className="px-3 py-1 border border-slate-300 rounded text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-slate-50"
                >
                  Suivant
                </button>
              </div>
            </div>
          )}
          
          {/* === VUE GRILLE (4 colonnes) ===
              lot AV3 — alignement : la grille en `items-stretch`, la carte en colonne flexible,
              le corps qui absorbe l'espace, le bloc d'actions à hauteur fixe. Une carte peut
              afficher trois lignes d'information et sa voisine quatre : une hauteur fixe ne
              pourrait pas les aligner. */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 items-stretch">
          {sortedProperties.map(renderCarteBien)}
        </div>
        
        {/* Pagination du bas (grille) */}
        {sortedAndFilteredProperties.length > itemsPerPage && (
          <div className="mt-6 flex flex-wrap items-center justify-between gap-3 border-t border-slate-200 pt-4">
            <div className="flex items-center gap-3">
              <span className="text-sm text-slate-600">
                Affichage de {startIndex + 1}-{Math.min(startIndex + itemsPerPage, sortedAndFilteredProperties.length)} sur {sortedAndFilteredProperties.length} biens
              </span>
            </div>
            <div className="flex items-center gap-2">
              <button 
                onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                disabled={currentPage === 1}
                className="px-3 py-1 border border-slate-300 rounded text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-slate-50"
              >
                Précédent
              </button>
              <span className="text-sm text-slate-600">
                Page {currentPage} sur {totalPages}
              </span>
              <button 
                onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                disabled={currentPage === totalPages}
                className="px-3 py-1 border border-slate-300 rounded text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-slate-50"
              >
                Suivant
              </button>
            </div>
          </div>
        )}
      </>
      ) : (
        <>
          {/* Pagination du haut (liste) */}
          {sortedAndFilteredProperties.length > itemsPerPage && (
            <div className="flex items-center justify-between bg-white border border-slate-200 rounded-lg p-3 mb-4">
              <div className="flex items-center gap-3">
                <span className="text-sm text-slate-600">
                  Affichage de {startIndex + 1}-{Math.min(startIndex + itemsPerPage, sortedAndFilteredProperties.length)} sur {sortedAndFilteredProperties.length} biens
                </span>
                <select 
                  value={itemsPerPage} 
                  onChange={(e) => { setItemsPerPage(Number(e.target.value)); setCurrentPage(1); }}
                  className="border border-slate-300 rounded px-2 py-1 text-sm"
                >
                  <option value={10}>10 par page</option>
                  <option value={20}>20 par page</option>
                  <option value={50}>50 par page</option>
                  <option value={100}>100 par page</option>
                </select>
                
                {/* Sélecteur de tri */}
                <div className="flex items-center gap-2 ml-4 border-l border-slate-300 pl-4">
                  <label className="text-sm text-slate-600 font-medium">Trier par :</label>
                  <select 
                    value={sortColumn}
                    onChange={(e) => setSortColumn(e.target.value)}
                    className="px-3 py-1 border border-slate-300 rounded text-sm bg-white hover:border-blue-400 focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                  >
                    <option value="created_at">Date de création</option>
                    <option value="updated_at">Dernière modification</option>
                  </select>
                  
                  <button
                    onClick={() => setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')}
                    className="px-3 py-1 border border-slate-300 rounded text-sm bg-white hover:bg-slate-50 flex items-center gap-1"
                    title={sortOrder === 'asc' ? 'Ordre croissant' : 'Ordre décroissant'}
                  >
                    <i className={`bi ${sortOrder === 'asc' ? 'bi-sort-up' : 'bi-sort-down'} text-blue-600`}></i>
                    {sortOrder === 'asc' ? 'Croissant' : 'Décroissant'}
                  </button>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <button 
                  onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                  disabled={currentPage === 1}
                  className="px-3 py-1 border border-slate-300 rounded text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-slate-50"
                >
                  Précédent
                </button>
                <span className="text-sm text-slate-600">
                  Page {currentPage} sur {totalPages}
                </span>
                <button 
                  onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                  disabled={currentPage === totalPages}
                  className="px-3 py-1 border border-slate-300 rounded text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-slate-50"
                >
                  Suivant
                </button>
              </div>
            </div>
          )}
          
          {/* === VUE LISTE === */}
          <div className="bg-white border border-slate-200 rounded-xl overflow-hidden shadow-sm">
            <div className="overflow-x-auto">
              <table className="w-full">
              <thead className="bg-slate-100">
                <tr>
                  <th className="px-4 py-3 text-left"><input type="checkbox" checked={selectedProperties.length === sortedProperties.length} onChange={(e) => handleSelectAll(e.target.checked)} className="w-4 h-4 rounded text-blue-600" /></th>
                  <th className="px-4 py-3 text-left text-xs font-bold text-slate-600 uppercase">Photo</th>
                  <th className="px-4 py-3 text-left text-xs font-bold text-slate-600 uppercase cursor-pointer hover:bg-slate-200 transition" onClick={() => handleSort('reference')}>
                    <div className="flex items-center gap-2">
                      Référence
                      {sortColumn === 'reference' && (
                        <i className={`bi ${sortOrder === 'asc' ? 'bi-arrow-up' : 'bi-arrow-down'} text-blue-600`}></i>
                      )}
                    </div>
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-bold text-slate-600 uppercase cursor-pointer hover:bg-slate-200 transition" onClick={() => handleSort('type')}>
                    <div className="flex items-center gap-2">
                      Type / Activité
                      {sortColumn === 'type' && (
                        <i className={`bi ${sortOrder === 'asc' ? 'bi-arrow-up' : 'bi-arrow-down'} text-blue-600`}></i>
                      )}
                    </div>
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-bold text-slate-600 uppercase cursor-pointer hover:bg-slate-200 transition" onClick={() => handleSort('ville')}>
                    <div className="flex items-center gap-2">
                      Ville
                      {sortColumn === 'ville' && (
                        <i className={`bi ${sortOrder === 'asc' ? 'bi-arrow-up' : 'bi-arrow-down'} text-blue-600`}></i>
                      )}
                    </div>
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-bold text-slate-600 uppercase cursor-pointer hover:bg-slate-200 transition" onClick={() => handleSort('prix')}>
                    <div className="flex items-center gap-2">
                      Prix
                      {sortColumn === 'prix' && (
                        <i className={`bi ${sortOrder === 'asc' ? 'bi-arrow-up' : 'bi-arrow-down'} text-blue-600`}></i>
                      )}
                    </div>
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-bold text-slate-600 uppercase cursor-pointer hover:bg-slate-200 transition" onClick={() => handleSort('statut')}>
                    <div className="flex items-center gap-2">
                      Statut
                      {sortColumn === 'statut' && (
                        <i className={`bi ${sortOrder === 'asc' ? 'bi-arrow-up' : 'bi-arrow-down'} text-blue-600`}></i>
                      )}
                    </div>
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-bold text-slate-600 uppercase">Diffusion</th>
                  <th className="px-4 py-3 text-left text-xs font-bold text-slate-600 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {sortedProperties.map((property) => {
                  const statusBadge = getStatusBadge(property);
                  const isSelected = selectedProperties.includes(property.id);
                  const linkFromState = propertyLinks[property.id];
                  const link = linkFromState?.url ? linkFromState : (property.share_token ? { code: property.share_token, url: `${window.location.origin}/f/${property.share_token}`, loading: false } : null);
                  return (
                  <React.Fragment key={property.id}>
                    <tr className={`hover:bg-slate-50 ${isSelected ? 'bg-blue-50' : ''}`}>
                      <td className="px-4 py-3"><input type="checkbox" checked={isSelected} onChange={(e) => handleSelectProperty(property.id, e.target.checked)} className="w-4 h-4 rounded text-blue-600" /></td>
                      <td className="px-4 py-3">
                        <div className="w-16 h-12 rounded-lg overflow-hidden bg-slate-100">
                          {(() => {
                            const photoUrl = getPropertyMainPhotoUrl(property, 'thumb');
                            return photoUrl ? (
                              <img src={photoUrl} loading="lazy" alt="" className="w-full h-full object-cover" onError={(e) => { e.target.style.display = 'none'; }} />
                            ) : (
                              <div className="w-full h-full flex items-center justify-center"><i className="bi bi-building text-slate-300"></i></div>
                            );
                          })()}
                        </div>
                      </td>
                      <td className="px-4 py-3 text-xs font-mono text-slate-500">{property.reference || '—'}</td>
                      <td className="px-4 py-3">
                        <div className="text-sm font-semibold text-slate-800">{formatTypeBien(property)}</div>
                        <div className="text-xs text-slate-500">{formatOperation(property)}</div>
                      </td>
                      <td className="px-4 py-3 text-sm text-slate-600">{getVille(property)}</td>
                      <td className="px-4 py-3 text-sm font-bold text-blue-600 num">{formatPrice(property)}</td>
                      <td className="px-4 py-3"><span className={`text-[10px] px-2 py-1 rounded-lg font-semibold border ${statusBadge.class}`}>{statusBadge.label}</span></td>
                      {/* lot AV3 — colonne « Diffusion », même code couleur que les cartes. */}
                      <td className="px-4 py-3">
                        <DiffusionBadges etats={etatsDiffusion[property.id]} propertyId={property.id} />
                      </td>
                      <td className="px-4 py-3">
                        {/* lot AV6 — motif unique (bordure fine, fond blanc, icône colorée) et
                            `aria-label` sur chaque bouton d'icône seule : un lecteur d'écran
                            n'annonce rien avec un simple `title`. */}
                        <div className="flex items-center gap-1">
                          <button onClick={() => handleViewProperty(property)}
                            className="w-9 h-9 bg-white border border-slate-200 hover:bg-blue-50 rounded-lg flex items-center justify-center transition"
                            title="Voir" aria-label={`Voir le bien ${property.reference || ''}`.trim()}
                            data-testid={`row-voir-${property.id}`}>
                            <i className="bi bi-eye text-[#007BFF]"></i>
                          </button>
                          <button onClick={() => handleEditProperty(property)}
                            className="w-9 h-9 bg-white border border-slate-200 hover:bg-blue-50 rounded-lg flex items-center justify-center transition"
                            title="Modifier" aria-label={`Modifier le bien ${property.reference || ''}`.trim()}
                            data-testid={`row-modifier-${property.id}`}>
                            <i className="bi bi-pencil text-[#007BFF]"></i>
                          </button>
                          {aUnSupportModifie(etatsDiffusion[property.id]) && (
                            <button onClick={() => ouvrirGestionDiffusion(property)}
                              className="w-9 h-9 bg-white border border-amber-300 hover:bg-amber-50 rounded-lg flex items-center justify-center transition"
                              title="Renvoyer l'annonce" aria-label={`Renvoyer l'annonce du bien ${property.reference || ''}`.trim()}
                              data-testid={`row-renvoyer-${property.id}`}>
                              <i className="bi bi-arrow-repeat text-amber-700"></i>
                            </button>
                          )}
                          <div className="relative">
                            <button
                              onClick={(e) => { e.stopPropagation(); setActiveDropdown(activeDropdown === property.id ? null : property.id); }}
                              className="w-9 h-9 bg-white border border-slate-200 hover:bg-blue-50 text-slate-600 rounded-lg flex items-center justify-center transition"
                              title="Plus d'actions"
                              aria-label={`Plus d'actions pour le bien ${property.reference || ''}`.trim()}
                              data-testid={`property-kebab-${property.id}`}
                            >
                              <i className="bi bi-three-dots-vertical"></i>
                            </button>
                            {activeDropdown === property.id && renderKebabDropdown(property, 'down')}
                          </div>
                        </div>
                      </td>
                    </tr>
                    {/* Row d'expand pour afficher le lien de partage + QR en mode liste */}
                    {expandedShareProperty === property.id && link && (
                      <tr key={`${property.id}-expand`} className="bg-blue-50">
                        <td colSpan={9} className="px-4 py-3">
                          <div className="flex items-center justify-between gap-3">
                            <div className="flex items-center gap-3">
                              {link.qr_code && (
                                <div className="flex-shrink-0 bg-white p-1 rounded border border-blue-200">
                                  <img
                                    src={link.qr_code}
                                    alt="QR Code"
                                    className="w-14 h-14 block"
                                    data-testid={`share-qr-${property.id}`}
                                  />
                                </div>
                              )}
                              <div>
                                <p className="text-xs text-blue-700 font-semibold flex items-center gap-1.5">
                                  <i className="bi bi-link-45deg"></i>Lien de partage public actif
                                </p>
                                <p className="text-[10px] text-slate-500">{link.qr_code ? 'Scannez le QR pour partager' : 'Cliquez pour copier'}</p>
                              </div>
                            </div>
                            <div className="flex gap-2">
                              <button
                                onClick={(e) => { e.stopPropagation(); copyToClipboard(link.url); }}
                                className="px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white text-xs rounded font-semibold flex items-center gap-1.5"
                                data-testid={`share-copy-${property.id}`}
                              >
                                <i className="bi bi-clipboard"></i>Copier le lien
                              </button>
                              <a
                                href={link.url} target="_blank" rel="noopener noreferrer"
                                onClick={(e) => e.stopPropagation()}
                                className="px-3 py-1.5 bg-green-600 hover:bg-green-700 text-white text-xs rounded font-semibold flex items-center gap-1.5"
                                data-testid={`share-open-${property.id}`}
                              >
                                <i className="bi bi-box-arrow-up-right"></i>Voir la fiche
                              </a>
                              <button
                                onClick={(e) => { e.stopPropagation(); setExpandedShareProperty(null); }}
                                className="px-2 py-1.5 bg-slate-100 hover:bg-slate-200 text-slate-600 text-xs rounded"
                                title="Fermer"
                              >
                                <i className="bi bi-x-lg"></i>
                              </button>
                            </div>
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
        
        {/* Pagination du bas (liste) */}
        {sortedAndFilteredProperties.length > itemsPerPage && (
          <div className="mt-6 flex items-center justify-between border-t border-slate-200 pt-4">
            <div className="flex items-center gap-3">
              <span className="text-sm text-slate-600">
                Affichage de {startIndex + 1}-{Math.min(startIndex + itemsPerPage, sortedAndFilteredProperties.length)} sur {sortedAndFilteredProperties.length} biens
              </span>
              
              {/* Sélecteur de tri */}
              <div className="flex items-center gap-2 ml-6 border-l border-slate-300 pl-6">
                <label className="text-sm text-slate-600 font-medium">Trier par :</label>
                <select 
                  value={sortColumn}
                  onChange={(e) => setSortColumn(e.target.value)}
                  className="px-3 py-1 border border-slate-300 rounded text-sm bg-white hover:border-blue-400 focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                >
                  <option value="created_at">Date de création</option>
                  <option value="updated_at">Dernière modification</option>
                </select>
                
                <button
                  onClick={() => setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')}
                  className="px-3 py-1 border border-slate-300 rounded text-sm bg-white hover:bg-slate-50 flex items-center gap-1"
                  title={sortOrder === 'asc' ? 'Ordre croissant' : 'Ordre décroissant'}
                >
                  <i className={`bi ${sortOrder === 'asc' ? 'bi-sort-up' : 'bi-sort-down'} text-blue-600`}></i>
                  {sortOrder === 'asc' ? 'Croissant' : 'Décroissant'}
                </button>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <button 
                onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                disabled={currentPage === 1}
                className="px-3 py-1 border border-slate-300 rounded text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-slate-50"
              >
                Précédent
              </button>
              <span className="text-sm text-slate-600">
                Page {currentPage} sur {totalPages}
              </span>
              <button 
                onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                disabled={currentPage === totalPages}
                className="px-3 py-1 border border-slate-300 rounded text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-slate-50"
              >
                Suivant
              </button>
            </div>
          </div>
        )}
      </>
      )}

      {/* === LÉGÈREMENT AU-DESSUS DU BUDGET (lot AV10/P3 bis) ===
          Un acquéreur ne doit pas rater un bien à quelques pour cent au-dessus de son budget,
          mais ⛔ ces biens ne sont JAMAIS mélangés à la liste principale : le filtre reste
          celui que l'utilisateur a saisi. Le bloc disparaît s'il est vide. */}
      {moteurFacettes.presqueBudget.length > 0 && (
        <div className="mt-8 pt-6 border-t border-dashed border-amber-300" data-testid="bloc-presque-budget">
          <h2 className="text-base md:text-lg font-semibold text-slate-800 mb-3 flex items-center gap-2">
            <i className="bi bi-plus-slash-minus text-amber-500"></i>
            Légèrement au-dessus de votre {budgetNature === 'location' ? 'loyer' : 'budget'}
            <span className="tabular-nums" data-testid="presque-budget-compteur">
              ({moteurFacettes.presqueBudget.length})
            </span>
            <span className="text-xs font-normal text-slate-500">
              jusqu'à {Math.round((MARGE_BUDGET - 1) * 100)} % au-dessus
            </span>
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 items-stretch">
            {moteurFacettes.presqueBudget.map(renderCarteBien)}
          </div>
        </div>
      )}

      
      {/* Modale d'affectation client */}
      <AffectClientModal
        isOpen={showAffectClientModal}
        onClose={() => setShowAffectClientModal(false)}
        propertyIds={propertiesToAffect}
        onSuccess={handleAffectSuccess}
      />
      {sendFichesModalOpen && (
        <SendFichesModal
          propertyCount={selectedProperties.length}
          onClose={() => setSendFichesModalOpen(false)}
          onSubmit={submitBulkFiches}
        />
      )}
      {/* lot AV4/AV7 — récapitulatif avant envoi, jamais d'envoi silencieux. */}
      {lotDiffusion && (
        <DiffuserEnLotModal
          action={lotDiffusion.action}
          biens={lotDiffusion.biens}
          supportsDisponibles={supportsDiffusion}
          onClose={() => setLotDiffusion(null)}
          onTermine={chargerEtatsDiffusion}
        />
      )}
      {/* lot CR — un seul bien : la même fenêtre que la fiche, qui permet de couper un portail. */}
      {bienDiffusion && (
        <DiffusionManagerModal
          property={bienDiffusion}
          diffusion={bienDiffusion.diffusion}
          onClose={() => setBienDiffusion(null)}
          onChange={(nouvelle) => {
            setProperties(liste => liste.map(p => (
              p.id === bienDiffusion.id ? { ...p, diffusion: nouvelle } : p)));
            setBienDiffusion(bien => (bien ? { ...bien, diffusion: nouvelle } : bien));
            chargerEtatsDiffusion();
          }}
        />
      )}
    </div>
  );
};

export default PropertiesListPage;
